import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { adminFetchInit } from '../utils/adminToken';

/**
 * Same tint as the mockup preview SVG feColorMatrix: scale R/G/B by garment color
 * so a light/white template exports with the chosen shirt color.
 */
function applyMockupTemplateColorTint(canvas, hexColor) {
  const raw = (hexColor && String(hexColor).startsWith('#') ? hexColor : '#FFFFFF')
    .replace('#', '')
    .padEnd(6, '0')
    .substring(0, 6);
  let mr = parseInt(raw.substring(0, 2), 16);
  let mg = parseInt(raw.substring(2, 4), 16);
  let mb = parseInt(raw.substring(4, 6), 16);
  if (Number.isNaN(mr)) mr = 255;
  if (Number.isNaN(mg)) mg = 255;
  if (Number.isNaN(mb)) mb = 255;
  mr /= 255;
  mg /= 255;
  mb /= 255;
  if (mr === 1 && mg === 1 && mb === 1) return;

  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    data[i] = Math.min(255, Math.round(data[i] * mr));
    data[i + 1] = Math.min(255, Math.round(data[i + 1] * mg));
    data[i + 2] = Math.min(255, Math.round(data[i + 2] * mb));
  }
  ctx.putImageData(imageData, 0, 0);
}

const AdminOrder = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [creatingOrder, setCreatingOrder] = useState(false);
  
  // Modal state
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showProductBrowser, setShowProductBrowser] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [customItem, setCustomItem] = useState({
    name: '',
    price: '',
    quantity: 1,
    taxable: false,
    physical: false,
    weight: 0
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedViewOrder, setSelectedViewOrder] = useState(null);
  const [selectedViewOrderPrintful, setSelectedViewOrderPrintful] = useState(null);
  const [loadingPrintful, setLoadingPrintful] = useState(false);
  const [orderDetailTab, setOrderDetailTab] = useState('shopify');
  const [printfulOrderIdInput, setPrintfulOrderIdInput] = useState('');
  
  // Confirmation modal states
  const [showFulfillOrderModal, setShowFulfillOrderModal] = useState(false);
  const [orderToFulfill, setOrderToFulfill] = useState(null);

  // Bulk selection state
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());

  // Cancel order modal state
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [cancellingOrderIds, setCancellingOrderIds] = useState([]);
  const [cancellingInProgress, setCancellingInProgress] = useState(false);
  const [cancelForm, setCancelForm] = useState({
    refundMethod: 'original',
    reason: 'customer',
    staffNote: '',
    restock: true,
    notifyCustomer: true
  });

  // Real products from Shopify API
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Mockup Editor State (for Edit Order)
  const [showMockupEditorModal, setShowMockupEditorModal] = useState(false);
  const [mockupProductType, setMockupProductType] = useState('tshirt');
  const [mockupSize, setMockupSize] = useState('XS');
  const [mockupColor, setMockupColor] = useState('#FFFFFF');
  const [mockupPositionX, setMockupPositionX] = useState(50);
  const [mockupPositionY, setMockupPositionY] = useState(50);
  const [mockupScale, setMockupScale] = useState(100);
  const [savingMockup, setSavingMockup] = useState(false);
  const [currentMockupImage, setCurrentMockupImage] = useState(null);
  const [currentMockupPublicId, setCurrentMockupPublicId] = useState(null);
  const mockupPreviewRef = React.useRef(null);

  // Create Order Mockup state
  const [activeCreateDropdownId, setActiveCreateDropdownId] = useState(null);
  const [mockupEditorContext, setMockupEditorContext] = useState('edit');
  const createDropdownRef = useRef(null);

  // Product Templates
  const productTemplates = {
    tshirt: { title: 'T-Shirt', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725211/t-shirt.png' },
    mug: { title: 'Mug 11oz', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725116/mug-front.png' },
    poster: { title: 'Poster', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725125/poster-front.png' },
    cap: { title: 'Cap', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725092/cap-front.png' },
    totebag: { title: 'Tote Bag', image: 'https://res.cloudinary.com/dez2cotie/image/upload/v1770725155/totebag.png' },
  };

  const productTypes = [
    { id: 'tshirt', label: 'T-Shirt', icon: '👕' },
    { id: 'mug', label: 'Mug', icon: '☕' },
    { id: 'poster', label: 'Poster', icon: '🖼️' },
    { id: 'cap', label: 'Cap', icon: '🧢' },
    { id: 'totebag', label: 'Tote Bag', icon: '👜' },
  ];

  const getAvailableTypes = (products) => {
    if (!products || typeof products !== 'object' || Object.keys(products).length === 0) return productTypes;
    if (Object.values(products).every(v => v)) return productTypes;
    return productTypes.filter(pt => {
      const key = pt.id === 'cap' ? 'casquette' : pt.id;
      return products[key] === true;
    });
  };

  const REVERSE_TYPE_MAP = { '👕 T-Shirt': 'tshirt', '☕ Mug': 'mug', '🖼️ Poster': 'poster', '👜 Tote Bag': 'totebag', '🧢 Cap': 'cap' };

  const getSelectedLineItemTypes = () => {
    const types = new Set();
    (orderForm.lineItems || []).forEach(item => {
      if (item.selectedTypeId) {
        types.add(item.selectedTypeId);
      } else if (item.title) {
        const titleParts = item.title.split(' - ');
        if (titleParts.length >= 2) {
          const variantPart = titleParts.slice(1).join(' - ').split(' / ')[0].trim();
          const typeId = REVERSE_TYPE_MAP[variantPart];
          if (typeId) types.add(typeId);
        }
      }
    });
    if (types.size === 0) return null;
    return productTypes.filter(pt => types.has(pt.id));
  };

  const sizeOptionsByType = {
    tshirt: [
      { value: 'XS', label: 'XS' }, { value: 'S', label: 'S' }, { value: 'M', label: 'M' },
      { value: 'L', label: 'L' }, { value: 'XL', label: 'XL' }, { value: 'XXL', label: 'XXL' }, { value: '3XL', label: '3XL' }
    ],
    mug: [
      { value: '8oz', label: '8 oz (Small)' }, { value: '11oz', label: '11 oz (Standard)' },
      { value: '15oz', label: '15 oz (Large)' }, { value: '20oz', label: '20 oz (Extra Large)' }
    ],
    poster: [
      { value: 'A4', label: 'Small - A4 (2480 × 3508 px)' },
      { value: 'A3', label: 'Medium - A3 (3508 × 4961 px)' },
      { value: 'Large', label: 'Large (7200 × 10800 px)' }
    ],
    cap: [
      { value: 'S/M', label: 'S/M (54 – 58 cm)' }, { value: 'L/XL', label: 'L/XL (58 – 62 cm)' }
    ],
    totebag: [
      { value: 'Small', label: 'Small (3600 × 4200 px)' }, { value: 'Medium', label: 'Medium (3600 × 4200 px)' },
      { value: 'Large', label: 'Large (4200 × 4800 px)' }, { value: 'XL', label: 'Extra Large (4800 × 5400 px)' }
    ]
  };

  // Close create-order mockup dropdown when clicking outside
  useEffect(() => {
    function handleClickOutsideDropdown(event) {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setActiveCreateDropdownId(null);
      }
    }
    if (activeCreateDropdownId) {
      document.addEventListener('mousedown', handleClickOutsideDropdown);
    }
    return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
  }, [activeCreateDropdownId]);

  // Fetch products from Shopify
  const fetchShopifyProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch('/.netlify/functions/shopify-products?limit=50', adminFetchInit());
      const data = await response.json();
      
      if (data.success && data.products) {
        // Transform Shopify products to our format
        const transformedProducts = data.products.map(product => ({
          id: product.id,
          title: product.name || product.title,
          image: product.image || 'https://via.placeholder.com/150/E5E7EB/6B7280?text=No+Image',
          vendor: product.vendor || '',
          variants: product.variants ? product.variants.map(variant => ({
            id: variant.id,
            title: variant.selectedOptions?.map(opt => opt.value).join(' / ') || 'Default',
            price: parseFloat(variant.price || 0),
            currency: "EUR",
            available: variant.quantityAvailable || 0,
            selectedOptions: variant.selectedOptions || []
          })) : [{
            id: `${product.id}-default`,
            title: "Default",
            price: parseFloat(product.price || 0),
            currency: "EUR", 
            available: product.stock || 0
          }]
        }));
        
        setShopifyProducts(transformedProducts);
        console.log('✅ Fetched Shopify products:', transformedProducts.length);
      } else {
        console.error('Failed to fetch Shopify products:', data.error);
        // Fallback to sample data if API fails
        setShopifyProducts(getSampleProducts());
      }
    } catch (error) {
      console.error('Error fetching Shopify products:', error);
      // Fallback to sample data if API fails
      setShopifyProducts(getSampleProducts());
    } finally {
      setLoadingProducts(false);
    }
  }, []);



  // Sample products fallback (used if Shopify API fails)
  const getSampleProducts = () => [
    {
      id: 1,
      title: "T-shirt Bob le Gob test",
      image: "https://via.placeholder.com/150/FF6B6B/FFFFFF?text=T-Shirt",
      variants: [
        { id: 11, title: "XS", price: 0.00, currency: "EUR", available: 0 },
        { id: 12, title: "S", price: 0.00, currency: "EUR", available: 0 },
        { id: 13, title: "M", price: 0.00, currency: "EUR", available: 0 },
        { id: 14, title: "L", price: 0.00, currency: "EUR", available: 0 },
        { id: 15, title: "XL", price: 0.00, currency: "EUR", available: 0 },
        { id: 16, title: "2XL", price: 0.00, currency: "EUR", available: 0 }
      ]
    },
    {
      id: 2,
      title: "T-shirt Bob4ever",
      image: "https://via.placeholder.com/150/4ECDC4/FFFFFF?text=Bob4ever",
      variants: [
        { id: 21, title: "Default", price: 34.90, currency: "EUR", available: 4 }
      ]
    },
    {
      id: 3,
      title: "test from manual push",
      image: "https://via.placeholder.com/150/45B7D1/FFFFFF?text=Test+Item",
      variants: [
        { id: 31, title: "Default", price: 1.00, currency: "EUR", available: 0 }
      ]
    }
  ];
  
  // Orders and draft orders data
  const [orders, setOrders] = useState([]);
  const [draftOrders, setDraftOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('paid');
  const [printfulSubTab, setPrintfulSubTab] = useState('draft');
  const [selectedPrintfulIds, setSelectedPrintfulIds] = useState(new Set());
  const [cancellingPrintful, setCancellingPrintful] = useState(false);

  const [printfulAdminOrders, setPrintfulAdminOrders] = useState([]);
  const [loadingPrintfulAdmin, setLoadingPrintfulAdmin] = useState(false);
  const [printfulCleanupRunning, setPrintfulCleanupRunning] = useState(false);
  const [showPrintfulCleanupConfirm, setShowPrintfulCleanupConfirm] = useState(false);
  const [printfulRowDetailModal, setPrintfulRowDetailModal] = useState({
    open: false,
    loading: false,
    order: null
  });

  // Order form state
  const [orderForm, setOrderForm] = useState({
    lineItems: [{
      title: '',
      price: '',
      quantity: 1,
      grams: 1000,
      variant_id: null
    }],
    currency: 'EUR',
    totalTax: 0,
    email: '',
    note: '',
    // Shipping and billing information
    first_name: '',
    last_name: '',
    address1: '',
    phone: '',
    city: '',
    province: '',
    country: '',
    zip: ''
  });



  // Show toast notification
  const showToastMessage = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  }, []);

  // Helper function to extract Shopify error messages
  const extractShopifyErrors = (errorResponse) => {
    if (!errorResponse) return 'Unknown error occurred';
    
    // If it's a string, return it directly
    if (typeof errorResponse === 'string') {
      return errorResponse;
    }
    
    // If it has an errors object (like Shopify validation errors)
    if (errorResponse.errors && typeof errorResponse.errors === 'object') {
      const errors = [];
      
      // Extract errors from the errors object
      Object.keys(errorResponse.errors).forEach(key => {
        const fieldErrors = errorResponse.errors[key];
        if (Array.isArray(fieldErrors)) {
          fieldErrors.forEach(error => {
            errors.push(`${key}: ${error}`);
          });
        } else if (typeof fieldErrors === 'string') {
          errors.push(`${key}: ${fieldErrors}`);
        }
      });
      
      if (errors.length > 0) {
        return errors.join('; ');
      }
    }
    
    // If it has a message property
    if (errorResponse.message) {
      return errorResponse.message;
    }
    
    // If it has an error property
    if (errorResponse.error) {
      return typeof errorResponse.error === 'string' ? errorResponse.error : JSON.stringify(errorResponse.error);
    }
    
    // Fallback - stringify the whole object
    return JSON.stringify(errorResponse);
  };

  // Fetch orders and draft orders from Shopify API
  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const [ordersRes, draftsRes] = await Promise.all([
        fetch('/.netlify/functions/get-orders', adminFetchInit({ method: 'GET' })),
        fetch('/.netlify/functions/get-draft-orders', adminFetchInit({ method: 'GET' }))
      ]);

      const ordersData = await ordersRes.json();
      const draftsData = await draftsRes.json();

      if (ordersData.success) {
        setOrders(ordersData.orders || []);
      } else {
        console.error('Failed to fetch orders:', ordersData.error);
        setOrders([]);
      }

      if (draftsData.success) {
        setDraftOrders(draftsData.draftOrders || []);
      } else {
        console.error('Failed to fetch draft orders:', draftsData.error);
        setDraftOrders([]);
      }

      setSelectedOrderIds(new Set());
      if (ordersData.success || draftsData.success) {
        showToastMessage(`Fetched ${ordersData.orders?.length || 0} orders, ${draftsData.draftOrders?.length || 0} drafts`, 'success');
      }
      if (!ordersData.success && !draftsData.success) {
        showToastMessage(`Failed to fetch: ${ordersData.error || draftsData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToastMessage('Failed to fetch - Network error', 'error');
      setOrders([]);
      setDraftOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [showToastMessage]);

  const fetchPrintfulAdminOrders = useCallback(async () => {
    setLoadingPrintfulAdmin(true);
    try {
      const res = await fetch('/.netlify/functions/printful-cleanup-orders', adminFetchInit());
      const data = await res.json();
      if (data.success) {
        setPrintfulAdminOrders(data.orders || []);
      } else {
        showToastMessage(data.message || data.error || 'Failed to load Printful orders', 'error');
      }
    } catch (e) {
      showToastMessage(e?.message || 'Failed to load Printful orders', 'error');
    } finally {
      setLoadingPrintfulAdmin(false);
    }
  }, [showToastMessage]);

  const executePrintfulCleanupAll = useCallback(async () => {
    setPrintfulCleanupRunning(true);
    try {
      const res = await fetch('/.netlify/functions/printful-cleanup-orders', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({})
      }));
      const data = await res.json();
      if (data.success) {
        const removed = data.removed ?? ((data.deleted || 0) + (data.cancelled || 0));
        const msg = `Printful: ${removed} removed (${data.deleted || 0} deleted via API v2, ${data.cancelled || 0} v1 cancel fallback), ${data.skipped} skipped, ${data.failed} failed`;
        showToastMessage(msg, data.failed > 0 ? 'error' : 'success');
        await fetchPrintfulAdminOrders();
      } else {
        showToastMessage(data.error || data.message || 'Cleanup failed', 'error');
      }
    } catch (e) {
      showToastMessage(e?.message || 'Cleanup failed', 'error');
    } finally {
      setPrintfulCleanupRunning(false);
      setShowPrintfulCleanupConfirm(false);
    }
  }, [fetchPrintfulAdminOrders, showToastMessage]);

  const cancelSelectedPrintfulOrders = useCallback(async () => {
    if (selectedPrintfulIds.size === 0) return;
    setCancellingPrintful(true);
    try {
      const ids = [...selectedPrintfulIds];
      const res = await fetch('/.netlify/functions/printful-cleanup-orders', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({ orderIds: ids })
      }));
      const data = await res.json();
      if (data.success) {
        const removed = data.removed ?? ((data.deleted || 0) + (data.cancelled || 0));
        showToastMessage(`${removed} Printful order(s) cancelled`, data.failed > 0 ? 'error' : 'success');
        setSelectedPrintfulIds(new Set());
        await fetchPrintfulAdminOrders();
      } else {
        showToastMessage(data.error || 'Cancel failed', 'error');
      }
    } catch (e) {
      showToastMessage(e?.message || 'Cancel failed', 'error');
    } finally {
      setCancellingPrintful(false);
    }
  }, [selectedPrintfulIds, fetchPrintfulAdminOrders, showToastMessage]);

  const openPrintfulRowDetail = useCallback(
    async (row) => {
      if (!row?.id) return;
      setPrintfulRowDetailModal({ open: true, loading: true, order: null });
      try {
        const res = await fetch(
          `/.netlify/functions/get-printful-order?printful_order_id=${encodeURIComponent(row.id)}`,
          adminFetchInit()
        );
        const data = await res.json();
        if (data.success && data.order) {
          setPrintfulRowDetailModal({ open: true, loading: false, order: data.order });
        } else {
          showToastMessage(data.error || 'Could not load Printful order', 'error');
          setPrintfulRowDetailModal({ open: false, loading: false, order: null });
        }
      } catch (e) {
        showToastMessage(e?.message || 'Failed to load', 'error');
        setPrintfulRowDetailModal({ open: false, loading: false, order: null });
      }
    },
    [showToastMessage]
  );

  const closePrintfulRowDetail = useCallback(() => {
    setPrintfulRowDetailModal({ open: false, loading: false, order: null });
  }, []);

  // Update order
  const updateOrder = async (orderId, updateData) => {
    try {
      const response = await fetch('/.netlify/functions/shopify-update-order', adminFetchInit({
        method: 'PUT',
        body: JSON.stringify({
          order: {
            id: orderId,
            ...updateData
          }
        })
      }));

      const data = await response.json();

      if (data.success) {
        showToastMessage('Order updated successfully', 'success');
        // Refresh orders list
        await fetchOrders();
        return true;
      } else {
        console.error('Failed to update order:', data.error);
        showToastMessage(`Failed to update order: ${data.error}`, 'error');
        return false;
      }
    } catch (error) {
      console.error('Error updating order:', error);
      showToastMessage('Failed to update order - Network error', 'error');
      return false;
    }
  };

  // Fulfill order - show confirmation modal (needs order + printful for Printful fulfill)
  const fulfillOrder = (order, printfulOrder) => {
    setOrderToFulfill({ order: order || orders.find(o => o.id === order?.id), printfulOrder });
    setShowFulfillOrderModal(true);
  };

  const confirmFulfillOrder = async () => {
    if (!orderToFulfill) return;

    setShowFulfillOrderModal(false);
    const { order, printfulOrder } = orderToFulfill;
    setOrderToFulfill(null);

    try {
      showToastMessage('Fulfilling order...', 'info');

      if (printfulOrder?.id) {
        const res = await fetch('/.netlify/functions/printful-confirm-order', adminFetchInit({
          method: 'POST',
          body: JSON.stringify({ printful_order_id: printfulOrder.id })
        }));
        const data = await res.json();
        if (data.success) {
          showToastMessage('Order submitted to Printful for fulfillment!', 'success');
          await fetchOrders();
          await fetchPrintfulAdminOrders();
          if (selectedViewOrder?.id === order?.id && data.order) {
            setSelectedViewOrderPrintful(data.order);
          }
        } else {
          showToastMessage(`Printful fulfillment failed: ${data.error}`, 'error');
        }
      } else {
        showToastMessage('This order has no Printful data. Fulfillment via Printful only.', 'error');
      }
    } catch (error) {
      console.error('Error fulfilling order:', error);
      showToastMessage('Failed to fulfill order', 'error');
    }
  };

  // Bulk selection handlers
  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.size === filteredOrders.length && filteredOrders.length > 0) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(filteredOrders.map(o => o.id)));
    }
  };

  // Open cancel order modal — accepts an array of order IDs (or a single one)
  const openCancelModal = (orderIds) => {
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const nonDraftIds = ids.filter(id => !String(id).startsWith('draft_'));
    if (nonDraftIds.length === 0) {
      showToastMessage('Draft orders cannot be cancelled via Shopify', 'error');
      return;
    }
    setCancellingOrderIds(nonDraftIds);
    setCancelForm({ refundMethod: 'original', reason: 'customer', staffNote: '', restock: true, notifyCustomer: true });
    setShowCancelOrderModal(true);
  };

  const confirmCancelOrders = async () => {
    if (cancellingOrderIds.length === 0) return;
    setCancellingInProgress(true);

    try {
      const response = await fetch('/.netlify/functions/shopify-cancel-order', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          orderIds: cancellingOrderIds,
          reason: cancelForm.reason,
          notifyCustomer: cancelForm.notifyCustomer,
          restock: cancelForm.restock,
          staffNote: cancelForm.staffNote || undefined,
          refundMethod: cancelForm.refundMethod
        })
      }));

      const data = await response.json();

      if (data.success) {
        showToastMessage(`${cancellingOrderIds.length} order(s) cancelled successfully`, 'success');
      } else {
        const failCount = (data.results || []).filter(r => !r.success).length;
        showToastMessage(data.message || `${failCount} order(s) failed to cancel`, failCount > 0 ? 'error' : 'success');
      }

      // Also cancel matching Printful orders by external_id
      // Printful external_id can be the Shopify numeric order ID OR the order name/number
      try {
        const pfRes = await fetch('/.netlify/functions/printful-cleanup-orders', adminFetchInit());
        const pfData = await pfRes.json();
        if (pfData.success && pfData.orders?.length > 0) {
          const matchSet = new Set();
          cancellingOrderIds.forEach(id => {
            matchSet.add(String(id));
            const order = allItems.find(o => o.id === id);
            if (order) {
              const num = (order.orderNumber || order.name || '').replace(/^#/, '').trim();
              if (num) matchSet.add(num);
            }
          });
          const pfIdsToCancel = pfData.orders
            .filter(o => matchSet.has(String(o.external_id)))
            .map(o => o.id);
          if (pfIdsToCancel.length > 0) {
            await fetch('/.netlify/functions/printful-cleanup-orders', adminFetchInit({
              method: 'POST',
              body: JSON.stringify({ orderIds: pfIdsToCancel })
            }));
            showToastMessage(`Also cancelled ${pfIdsToCancel.length} matching Printful order(s)`, 'success');
          }
        }
      } catch (pfErr) {
        console.warn('Printful auto-cancel skipped:', pfErr);
      }

      setShowCancelOrderModal(false);
      setCancellingOrderIds([]);
      setSelectedOrderIds(new Set());
      if (showViewModal) {
        setShowViewModal(false);
        setSelectedViewOrder(null);
      }
      await fetchOrders();
    } catch (error) {
      console.error('Error cancelling orders:', error);
      showToastMessage('Failed to cancel order(s) — network error', 'error');
    } finally {
      setCancellingInProgress(false);
    }
  };

  // Fetch Printful data for the currently selected order (used by openViewModal and refreshPrintful)
  const fetchPrintfulForOrder = useCallback(async (order) => {
    if (!order) return;
    const shopifyOrderId = order.isDraft && order.orderId ? order.orderId : order.id;
    if (!shopifyOrderId) return;
    const label = String(order.orderNumber || order.name || '').replace(/^#/, '').trim();
    const tryIds = [];
    if (/^\d+$/.test(label)) tryIds.push(label);
    tryIds.push(String(shopifyOrderId));
    const seen = new Set();
    for (const sid of tryIds) {
      if (!sid || seen.has(sid)) continue;
      seen.add(sid);
      const params = new URLSearchParams({ shopify_order_id: sid });
      if (order.email) params.set('email', order.email);
      const res = await fetch(`/.netlify/functions/get-printful-order?${params}`, adminFetchInit());
      const data = await res.json();
      if (data.success && data.order) return data.order;
    }
    return null;
  }, []);

  // Open view modal - fetch Printful data when opening
  const openViewModal = async (order) => {
    setSelectedViewOrder(order);
    setSelectedViewOrderPrintful(null);
    setOrderDetailTab('shopify');
    setShowViewModal(true);
    setLoadingPrintful(true);
    try {
      const pfOrder = await fetchPrintfulForOrder(order);
      if (pfOrder) setSelectedViewOrderPrintful(pfOrder);
      // If draft and no match, try draftId as fallback (Printful may use draft ID as external_id)
      if (!pfOrder && order.isDraft && order.draftId) {
        const params = new URLSearchParams({ shopify_order_id: String(order.draftId) });
        if (order.email) params.set('email', order.email);
        const res2 = await fetch(`/.netlify/functions/get-printful-order?${params}`, adminFetchInit());
        const data2 = await res2.json();
        if (data2.success && data2.order) setSelectedViewOrderPrintful(data2.order);
      }
    } catch (err) {
      console.warn('Failed to fetch Printful order:', err);
    } finally {
      setLoadingPrintful(false);
    }
  };

  // Refresh Printful data from Printful API (e.g. when "No Printful" but order exists in Printful)
  const refreshPrintful = async () => {
    if (!selectedViewOrder) return;
    setLoadingPrintful(true);
    try {
      const pfOrder = await fetchPrintfulForOrder(selectedViewOrder);
      if (pfOrder) {
        setSelectedViewOrderPrintful(pfOrder);
        showToastMessage('Printful data loaded successfully', 'success');
      } else if (selectedViewOrder.isDraft && selectedViewOrder.draftId) {
        const params = new URLSearchParams({ shopify_order_id: String(selectedViewOrder.draftId) });
        if (selectedViewOrder.email) params.set('email', selectedViewOrder.email);
        const res = await fetch(`/.netlify/functions/get-printful-order?${params}`, adminFetchInit());
        const data = await res.json();
        if (data.success && data.order) {
          setSelectedViewOrderPrintful(data.order);
          showToastMessage('Printful data loaded successfully', 'success');
        } else {
          showToastMessage('No Printful order found. Check Printful dashboard or external_id.', 'error');
        }
      } else {
        showToastMessage('No Printful order found. Check Printful dashboard or external_id.', 'error');
      }
    } catch (err) {
      console.warn('Refresh Printful failed:', err);
      showToastMessage('Failed to fetch from Printful', 'error');
    } finally {
      setLoadingPrintful(false);
    }
  };

  // Fetch Printful order by Printful order ID (from Printful dashboard)
  const fetchByPrintfulOrderId = async () => {
    const id = (printfulOrderIdInput || '').trim();
    if (!id) return;
    setLoadingPrintful(true);
    try {
      const res = await fetch(`/.netlify/functions/get-printful-order?printful_order_id=${encodeURIComponent(id)}`, adminFetchInit());
      const data = await res.json();
      if (data.success && data.order) {
        setSelectedViewOrderPrintful(data.order);
        setPrintfulOrderIdInput('');
        showToastMessage('Printful order loaded', 'success');
      } else {
        showToastMessage(data.error || 'Printful order not found', 'error');
      }
    } catch (err) {
      showToastMessage('Failed to fetch Printful order', 'error');
    } finally {
      setLoadingPrintful(false);
    }
  };

  // Close view modal
  const closeViewModal = () => {
    setSelectedViewOrder(null);
    setSelectedViewOrderPrintful(null);
    setPrintfulOrderIdInput('');
    setShowViewModal(false);
  };

  // Load orders and products on component mount
  useEffect(() => {
    fetchOrders();
    fetchShopifyProducts();
  }, [fetchOrders, fetchShopifyProducts]);

  useEffect(() => {
    if (activeTab !== 'printful') return;
    /* auth via HttpOnly cookie + adminFetchInit */
    fetchPrintfulAdminOrders();
  }, [activeTab, fetchPrintfulAdminOrders]);

  useEffect(() => {
    if (activeTab !== 'printful') return;
    if (printfulSubTab === 'canceled' || printfulSubTab === 'archived') {
      setPrintfulSubTab('printful_canceled_order');
    }
  }, [activeTab, printfulSubTab]);

  useEffect(() => {
    if (activeTab !== 'printful') return;
    if (
      printfulSubTab === 'printful_canceled_order' ||
      printfulSubTab === 'canceled' ||
      printfulSubTab === 'archived'
    ) {
      setSelectedPrintfulIds(new Set());
    }
  }, [activeTab, printfulSubTab]);

  useEffect(() => {
    if (activeTab === 'canceled_order') setSelectedOrderIds(new Set());
  }, [activeTab]);

  // Combined list: draft orders + orders
  const allItems = [
    ...draftOrders.map(d => ({ ...d, lineItems: d.lineItems || [] })),
    ...orders
  ].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  const isShopifyOrderCanceled = (item) => {
    if (item.isDraft) return false;
    if (item.cancelledAt) return true;
    const fs = (item.financialStatus || '').toLowerCase();
    return fs === 'voided';
  };

  const isOrderRefunded = (item) => {
    if (item.isDraft) return false;
    const fs = (item.financialStatus || '').toLowerCase();
    return fs === 'refunded' || fs === 'partially_refunded';
  };

  const isOrderArchived = (item) => {
    if (item.isDraft) return false;
    return !!item.closedAt;
  };

  const isInactiveOrderTab = (item) =>
    isShopifyOrderCanceled(item) || isOrderRefunded(item) || isOrderArchived(item);

  /** Pills next to order # on the “Canceled order” tab — Shopify-style (can combine). */
  const OrderLifecycleBadges = ({ order }) => {
    if (order.isDraft) return null;
    const fs = (order.financialStatus || '').toLowerCase();
    const refundedLabel = fs === 'partially_refunded' ? 'Partially refunded' : 'Refunded';
    const showCanceled = isShopifyOrderCanceled(order);
    const showRefunded = isOrderRefunded(order);
    const showArchived = isOrderArchived(order);
    if (!showCanceled && !showRefunded && !showArchived) return null;
    const pill =
      'inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold leading-none';
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0" aria-label="Order status">
        {showCanceled && (
          <span
            className={`${pill} border-orange-400/40 bg-orange-950/55 text-orange-100 shadow-sm shadow-orange-900/20`}
          >
            Canceled
          </span>
        )}
        {showRefunded && (
          <span
            className={`${pill} border-slate-400/35 bg-slate-800/80 text-slate-100 shadow-sm shadow-black/20`}
          >
            {refundedLabel}
          </span>
        )}
        {showArchived && (
          <span
            className={`${pill} border-gray-500/40 bg-gray-800/90 text-gray-100 shadow-sm shadow-black/15`}
          >
            Archived
          </span>
        )}
      </span>
    );
  };

  // Filter orders based on search and tab
  const filteredOrders = allItems.filter(item => {
    const matchesSearch = !searchTerm || 
      item.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.shippingAddress?.first_name && item.shippingAddress.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.shippingAddress?.last_name && item.shippingAddress.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.lineItems || []).some(li => li.title?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Tab-based filtering (Shopify: displayFinancialStatus / displayFulfillmentStatus / closedAt / cancelledAt)
    let matchesTab = true;
    if (activeTab === 'printful') {
      matchesTab = false;
    } else if (item.isDraft) {
      matchesTab = false;
    } else {
      const fs = (item.financialStatus || '').toLowerCase();
      const ff = (item.fulfillmentStatus || '').toLowerCase() || '';
      const isRefunded = isOrderRefunded(item);
      const isPaidFinancial = fs === 'paid' || fs === 'partially_paid';
      const isFulfilledStatus =
        ff === 'fulfilled' || ff === 'partially_fulfilled' || ff === 'partial';
      const isArchivedShopify = isOrderArchived(item);
      const canceled = isShopifyOrderCanceled(item);

      switch (activeTab) {
        case 'paid':
          matchesTab =
            isPaidFinancial && !isInactiveOrderTab(item);
          break;
        case 'fulfilled':
          matchesTab =
            isFulfilledStatus && !canceled && !isArchivedShopify && !isRefunded;
          break;
        case 'unfulfilled':
          matchesTab =
            !isFulfilledStatus && !canceled && !isRefunded && !isArchivedShopify;
          break;
        case 'canceled_order':
          matchesTab = isRefunded || canceled || isArchivedShopify;
          break;
        default:
          matchesTab = true;
      }
    }
    
    return matchesSearch && matchesTab;
  });

  // Format date - Shopify style (e.g. "Today at 3:06 am" or "Jan 14, 2026")
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    
    if (isToday) return `Today at ${time}`;
    if (isYesterday) return `Yesterday at ${time}`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get customer name from order
  const getCustomerName = (order) => {
    if (order.shippingAddress) {
      const first = order.shippingAddress.first_name || '';
      const last = order.shippingAddress.last_name || '';
      if (first || last) return `${first} ${last}`.trim();
    }
    if (order.billingAddress) {
      const first = order.billingAddress.first_name || '';
      const last = order.billingAddress.last_name || '';
      if (first || last) return `${first} ${last}`.trim();
    }
    return order.email || 'No customer';
  };

  // Get payment status badge - Shopify style
  const getPaymentBadge = (status) => {
    switch (status) {
      case 'paid':
        return { label: 'Paid', bg: 'bg-gray-700', text: 'text-gray-300', dot: 'bg-gray-400', complete: true };
      case 'pending':
        return { label: 'Payment pending', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      case 'authorized':
        return { label: 'Authorized', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      case 'partially_paid':
        return { label: 'Partially paid', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      case 'partially_refunded':
        return { label: 'Partially refunded', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      case 'refunded':
        return { label: 'Refunded', bg: 'bg-red-900/40', text: 'text-red-300', dot: 'bg-red-400', complete: false };
      case 'voided':
        return { label: 'Voided', bg: 'bg-red-900/40', text: 'text-red-300', dot: 'bg-red-400', complete: false };
      default:
        return { label: status || 'Unknown', bg: 'bg-gray-700', text: 'text-gray-300', dot: 'bg-gray-400', complete: false };
    }
  };

  // Get draft order status badge (open, invoice_sent, completed)
  const getDraftStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { label: 'Completed', bg: 'bg-gray-700', text: 'text-gray-300', dot: 'bg-gray-400', complete: true };
      case 'invoice_sent':
        return { label: 'Invoice sent', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      case 'open':
      default:
        return { label: 'Open', bg: 'bg-gray-700', text: 'text-gray-300', dot: 'bg-gray-400', complete: false };
    }
  };

  // Get fulfillment status badge - Shopify style
  const getFulfillmentBadge = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'fulfilled':
        return { label: 'Fulfilled', bg: 'bg-gray-700', text: 'text-gray-300', dot: 'bg-gray-400', complete: true };
      case 'partial':
      case 'partially_fulfilled':
        return { label: 'Partially fulfilled', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      case 'unfulfilled':
      case '':
        return { label: 'Unfulfilled', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
      default:
        return { label: s || status || 'Unfulfilled', bg: 'bg-yellow-900/40', text: 'text-yellow-300', dot: 'bg-yellow-400', complete: false };
    }
  };

  // Get total items count
  const getItemsCount = (lineItems) => {
    if (!lineItems || lineItems.length === 0) return '0 items';
    const total = lineItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    return `${total} item${total !== 1 ? 's' : ''}`;
  };

  // Get delivery method
  const getDeliveryMethod = (order) => {
    if (order.shippingAddress) return 'Shipping';
    return '—';
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setOrderForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Open edit modal
  const openEditModal = (order) => {
    setEditingOrder(order);
    
    // Extract Type, Size, Color, Quality from line item properties first
    const firstLineItem = (order.lineItems || [])[0];
    const lineItemProperties = firstLineItem?.properties || [];
    
    // Also check note_attributes (for orders updated via edit)
    const noteAttributes = order.note_attributes || [];
    
    // Prefer line item properties, fall back to note_attributes
    const getPropertyValue = (name, defaultValue = '') => {
      const lineItemProp = lineItemProperties.find(p => p.name === name);
      if (lineItemProp) return lineItemProp.value;
      const noteProp = noteAttributes.find(p => p.name === name);
      if (noteProp) return noteProp.value;
      return defaultValue;
    };
    
    // Initialize mockup image from existing note_attributes
    const existingImageAttr = noteAttributes.find(a => a.name === 'Sample Image' || a.name === 'MockupImage');
    setCurrentMockupImage(existingImageAttr?.value || null);
    
    // Try to extract public ID for deletion
    if (existingImageAttr?.value) {
      const match = existingImageAttr.value.match(/\/merch-platform\/order\/([^.]+)/);
      if (match) {
        setCurrentMockupPublicId(`merch-platform/order/${match[1]}`);
      } else {
        setCurrentMockupPublicId(null);
      }
    } else {
      setCurrentMockupPublicId(null);
    }
    
    // Initialize mockup state from existing values
    const typeMap = { 'T-Shirt': 'tshirt', 'Mug': 'mug', 'Poster': 'poster', 'Cap': 'cap', 'Tote Bag': 'totebag' };
    const existingType = getPropertyValue('Type', '');
    setMockupProductType(typeMap[existingType] || 'tshirt');
    setMockupSize(getPropertyValue('Size', ''));
    setMockupColor(getPropertyValue('Color', '#FFFFFF'));
    setMockupPositionX(parseInt(getPropertyValue('PositionX', '50')) || 50);
    setMockupPositionY(parseInt(getPropertyValue('PositionY', '50')) || 50);
    
    const mappedLineItems = (order.lineItems || []).length > 0
      ? (order.lineItems || []).map((li) => ({
          title: li.title || '',
          price: String(li.price ?? ''),
          quantity: li.quantity || 1,
          grams: 1000,
          variant_id: li.variant_id || null,
          product_id: li.product_id || null,
          product_legacy_id: li.product_legacy_id || null,
          image: li.image || null,
          properties: li.properties || [],
          vendor: li.vendor
        }))
      : [{
          title: '',
          price: '',
          quantity: 1,
          grams: 1000,
          variant_id: null,
          properties: []
        }];

    // Populate form with editable fields (lineItems required for mockup editor / type picker)
    setOrderForm({
      lineItems: mappedLineItems,
      currency: order.currency || 'EUR',
      totalTax: 0,
      email: order.email || '',
      phone: order.phone || '',
      note: order.note || '',
      tags: (order.tags && Array.isArray(order.tags)) ? order.tags.join(', ') : (order.tags || ''),
      buyerAcceptsMarketing: order.buyerAcceptsMarketing || false,
      productType: getPropertyValue('Type', ''),
      size: getPropertyValue('Size', ''),
      color: getPropertyValue('Color', '#000000'),
      first_name: order.shippingAddress?.first_name || order.shippingAddress?.firstName || '',
      last_name: order.shippingAddress?.last_name || order.shippingAddress?.lastName || '',
      address1: order.shippingAddress?.address1 || '',
      city: order.shippingAddress?.city || '',
      province: order.shippingAddress?.province || '',
      country: order.shippingAddress?.country || '',
      zip: order.shippingAddress?.zip || ''
    });
    setShowEditOrderModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditOrderModal(false);
    setEditingOrder(null);
    resetForm();
  };

  // Open mockup editor for editing existing mockup
  const openMockupEditor = () => {
    setMockupEditorContext('edit');
    const noteAttrs = editingOrder?.note_attributes || [];
    const typeAttr = noteAttrs.find(a => a.name === 'Type');
    const sizeAttr = noteAttrs.find(a => a.name === 'Size');
    const colorAttr = noteAttrs.find(a => a.name === 'Color');
    const imageAttr = noteAttrs.find(a => a.name === 'Sample Image' || a.name === 'MockupImage');
    const posXAttr = noteAttrs.find(a => a.name === 'PositionX');
    const posYAttr = noteAttrs.find(a => a.name === 'PositionY');
    
    const typeMap = { 'T-Shirt': 'tshirt', 'Mug': 'mug', 'Poster': 'poster', 'Cap': 'cap', 'Tote Bag': 'totebag' };
    const productTypeId = typeMap[typeAttr?.value] || 'tshirt';
    
    setMockupProductType(productTypeId);
    setMockupSize(sizeAttr?.value || orderForm.size || '');
    setMockupColor(colorAttr?.value || orderForm.color || '#FFFFFF');
    setMockupPositionX(posXAttr?.value ? parseInt(posXAttr.value) : 50);
    setMockupPositionY(posYAttr?.value ? parseInt(posYAttr.value) : 50);
    setMockupScale(100);
    setCurrentMockupImage(imageAttr?.value || null);
    
    if (imageAttr?.value) {
      const match = imageAttr.value.match(/\/merch-platform\/order\/([^.]+)/);
      if (match) {
        setCurrentMockupPublicId(`merch-platform/order/${match[1]}`);
      }
    }
    
    setShowMockupEditorModal(true);
  };

  // Open mockup editor for create order
  const openCreateMockupEditor = (productType) => {
    setMockupEditorContext('create');
    setActiveCreateDropdownId(null);
    handleProductTypeChangeWithPrice(productType);
    setMockupPositionX(50);
    setMockupPositionY(50);
    setMockupScale(100);
    setMockupSize('');
    setShowMockupEditorModal(true);
  };

  // Edit existing mockup during create order
  const handleEditCreateMockup = () => {
    if (currentMockupImage) {
      setMockupEditorContext('create');
      setShowMockupEditorModal(true);
    }
  };

  // Close mockup editor
  const closeMockupEditor = () => {
    setShowMockupEditorModal(false);
    setMockupPositionX(50);
    setMockupPositionY(50);
    setMockupScale(100);
  };

  // Generate mockup canvas data URL from current settings
  const generateMockupCanvas = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 800;
    canvas.height = 800;

    ctx.fillStyle = '#374151';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const templateImg = new Image();
    templateImg.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      templateImg.onload = resolve;
      templateImg.onerror = reject;
      templateImg.src = productTemplates[mockupProductType]?.image;
    });

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.drawImage(templateImg, 0, 0, tempCanvas.width, tempCanvas.height);
    applyMockupTemplateColorTint(tempCanvas, mockupColor);
    ctx.drawImage(tempCanvas, 0, 0);

    // Determine design image based on context
    let designImageSrc = null;
    if (mockupEditorContext === 'create') {
      designImageSrc = (orderForm.lineItems || []).find(item => item.image)?.image;
    } else {
      const lineItem = editingOrder?.lineItems?.[0];
      designImageSrc = lineItem ? (lineItem.image || getProductImage(lineItem)) : null;
    }

    if (designImageSrc && !designImageSrc.includes('placeholder')) {
      const designImg = new Image();
      designImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        designImg.onload = resolve;
        designImg.onerror = reject;
        designImg.src = designImageSrc;
      });

      const scale = mockupScale / 100;
      const designWidth = canvas.width * 0.5 * scale;
      const designHeight = canvas.height * 0.5 * scale;
      const designX = (canvas.width * mockupPositionX / 100) - (designWidth / 2);
      const designY = (canvas.height * mockupPositionY / 100) - (designHeight / 2);

      ctx.globalAlpha = 0.85;
      ctx.drawImage(designImg, designX, designY, designWidth, designHeight);
      ctx.globalAlpha = 1;
    }

    return canvas.toDataURL('image/png');
  };

  // Save mockup — for create order: keep locally; for edit order: upload to Cloudinary
  const handleSaveMockup = async () => {
    if (!mockupSize) {
      showToastMessage('Please select a size', 'error');
      return;
    }
    setSavingMockup(true);
    try {
      const imageData = await generateMockupCanvas();

      if (mockupEditorContext === 'create') {
        // For create order: save locally as data URL (no Cloudinary upload)
        const productTypeName = productTypes.find(p => p.id === mockupProductType)?.label || mockupProductType;
        handleFormChange('productType', productTypeName);
        handleFormChange('size', mockupSize);
        handleFormChange('color', mockupColor);

        setCurrentMockupImage(imageData);
        setCurrentMockupPublicId(null);

        showToastMessage('Mockup ready! It will be saved when the order is created.', 'success');
        setShowMockupEditorModal(false);
        return;
      }

      // For edit order: upload to Cloudinary immediately
      if (currentMockupPublicId) {
        try {
          await fetch('/.netlify/functions/cloudinary-delete-mockup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ publicId: currentMockupPublicId })
          });
        } catch (e) {
          console.warn('Failed to delete previous mockup:', e);
        }
      }

      const response = await fetch('/.netlify/functions/cloudinary-upload-mockup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData,
          folder: 'merch-platform/order'
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to upload mockup');
      }

      const productTypeName = productTypes.find(p => p.id === mockupProductType)?.label || mockupProductType;
      handleFormChange('productType', productTypeName);
      handleFormChange('size', mockupSize);
      handleFormChange('color', mockupColor);

      setCurrentMockupImage(result.url);
      setCurrentMockupPublicId(result.publicId);

      showToastMessage('Mockup updated successfully!', 'success');
      setShowMockupEditorModal(false);

    } catch (error) {
      console.error('Error saving mockup:', error);
      showToastMessage('Failed to save mockup: ' + error.message, 'error');
    } finally {
      setSavingMockup(false);
    }
  };

  // Handle edit order
  const handleEditOrder = async () => {
    try {
      setCreatingOrder(true);

      // Build note_attributes with product properties from mockup state
      const noteAttributes = [
        ...(editingOrder.note_attributes || []).filter(a =>
          !['Type', 'Size', 'Color', 'Sample Image', 'MockupImage', 'PositionX', 'PositionY'].includes(a.name)
        )
      ];
      
      // Use mockup state values (these are updated when mockup is edited)
      const productTypeName = productTypes.find(p => p.id === mockupProductType)?.label || '';
      if (productTypeName) noteAttributes.push({ name: 'Type', value: productTypeName });
      if (mockupSize) noteAttributes.push({ name: 'Size', value: mockupSize });
      if (mockupColor) noteAttributes.push({ name: 'Color', value: mockupColor });
      if (currentMockupImage) {
        noteAttributes.push({ name: 'Sample Image', value: currentMockupImage });
        noteAttributes.push({ name: 'PositionX', value: String(mockupPositionX) });
        noteAttributes.push({ name: 'PositionY', value: String(mockupPositionY) });
      }

      // Prepare update data - fields supported by shopify-update-order (Admin GraphQL orderUpdate)
      const updateData = {
        note: orderForm.note,
        email: orderForm.email,
        phone: orderForm.phone,
        tags: orderForm.tags,
        buyer_accepts_marketing: orderForm.buyerAcceptsMarketing,
        note_attributes: noteAttributes
      };

      const success = await updateOrder(editingOrder.id, updateData);
      
      if (success) {
        closeEditModal();
        showToastMessage('Order updated successfully!', 'success');
        // Refresh orders to show updated data
        await fetchOrders();
      }
    } catch (error) {
      console.error('❌ Error updating order:', error);
      showToastMessage('Error updating order', 'error');
    } finally {
      setCreatingOrder(false);
    }
  };

  // Handle line item changes (for create order only)
  const handleLineItemChange = (index, field, value) => {
    const filteredItems = orderForm.lineItems.filter(item => item.title.trim() !== '');
    const actualIndex = orderForm.lineItems.findIndex((item, i) => 
      item.title.trim() !== '' && filteredItems.indexOf(item) === index
    );
    
    if (actualIndex !== -1) {
      setOrderForm(prev => ({
        ...prev,
        lineItems: prev.lineItems.map((item, i) => 
          i === actualIndex ? { ...item, [field]: value } : item
        )
      }));
    }
  };



  // Remove line item (for create order only)
  const removeLineItem = (index) => {
    const filteredItems = orderForm.lineItems.filter(item => item.title.trim() !== '');
    const actualIndex = orderForm.lineItems.findIndex((item, i) => 
      item.title.trim() !== '' && filteredItems.indexOf(item) === index
    );
    
    if (actualIndex !== -1) {
      setOrderForm(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter((_, i) => i !== actualIndex)
      }));
    }
  };

  // Filter products based on search
  const filteredProducts = shopifyProducts.filter(product =>
    product.title.toLowerCase().includes(productSearch.toLowerCase())
  );

  // Handle product variant selection
  const handleVariantSelection = (product, variant, isSelected) => {
    if (isSelected) {
      setSelectedProducts(prev => [...prev, { product, variant }]);
    } else {
      setSelectedProducts(prev => 
        prev.filter(item => !(item.product.id === product.id && item.variant.id === variant.id))
      );
    }
  };

  // Check if variant is selected
  const isVariantSelected = (product, variant) => {
    return selectedProducts.some(item => 
      item.product.id === product.id && item.variant.id === variant.id
    );
  };

  // Add selected products to order
  const addSelectedProductsToOrder = () => {
    const newLineItems = selectedProducts.map(({ product, variant }) => {
      const typeOption = variant.selectedOptions?.find(o => o.name === 'Type');
      const selectedTypeId = typeOption ? REVERSE_TYPE_MAP[typeOption.value] || null : null;
      return {
        title: `${product.title} - ${variant.title}`,
        price: variant.price.toString(),
        quantity: 1,
        grams: 1000,
        variant_id: variant.id,
        image: product.image,
        vendor: product.vendor || '',
        availableProducts: product.products || null,
        selectedTypeId
      };
    });

    setOrderForm(prev => {
      // Remove empty custom items and add new products
      const filteredLineItems = prev.lineItems.filter(item => 
        item.title.trim() !== '' || item.price !== ''
      );
      
      return {
        ...prev,
        lineItems: [...filteredLineItems, ...newLineItems]
      };
    });

    setSelectedProducts([]);
    setProductSearch('');
    setShowProductBrowser(false);
  };

  // Cancel product selection
  const cancelProductSelection = () => {
    setSelectedProducts([]);
    setProductSearch('');
    setShowProductBrowser(false);
  };

  // Handle custom item
  const handleCustomItemChange = (field, value) => {
    setCustomItem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add custom item to order
  const addCustomItemToOrder = () => {
    if (!customItem.name.trim() || !customItem.price) {
      return;
    }

    const newLineItem = {
      title: customItem.name,
      price: customItem.price.toString(),
      quantity: customItem.quantity,
      grams: customItem.physical ? customItem.weight * 1000 : 0, // Convert kg to grams
      variant_id: null, // Custom items don't have variant_id
      taxable: customItem.taxable,
      isCustom: true,
      image: "https://via.placeholder.com/150/6B73FF/FFFFFF?text=Custom"
    };

    setOrderForm(prev => ({
      ...prev,
      lineItems: [...prev.lineItems.filter(item => item.title.trim() !== '' || item.price !== ''), newLineItem]
    }));

    // Reset custom item form
    setCustomItem({
      name: '',
      price: '',
      quantity: 1,
      taxable: false,
      physical: false,
      weight: 0
    });
    setShowCustomItemModal(false);
  };

  // Cancel custom item
  const cancelCustomItem = () => {
    setCustomItem({
      name: '',
      price: '',
      quantity: 1,
      taxable: false,
      physical: false,
      weight: 0
    });
    setShowCustomItemModal(false);
  };

  /** Prefer Shopify line item image; never guess by title alone when multiple products share a name. */
  const getProductImage = (item) => {
    if (item == null) {
      return 'https://via.placeholder.com/150/6B73FF/FFFFFF?text=Custom';
    }
    if (typeof item === 'object' && item.image) {
      return item.image;
    }

    const productTitle = typeof item === 'string' ? item : item.title;
    const vid = typeof item === 'object' && item.variant_id != null ? String(item.variant_id) : null;
    const pid = typeof item === 'object' && item.product_id != null ? String(item.product_id) : null;
    const legacyPid =
      typeof item === 'object' && item.product_legacy_id != null ? String(item.product_legacy_id) : null;

    if (vid && shopifyProducts.length) {
      const byVariant = shopifyProducts.find((p) =>
        p.variants?.some((v) => String(v.id) === vid)
      );
      if (byVariant?.image) return byVariant.image;
    }

    if ((pid || legacyPid) && shopifyProducts.length) {
      const byProduct = shopifyProducts.find((p) => {
        const idStr = p.id != null ? String(p.id) : '';
        return (pid && idStr === pid) || (legacyPid && idStr === legacyPid);
      });
      if (byProduct?.image) return byProduct.image;
    }

    const baseTitle =
      typeof item === 'object' ? productTitle?.split(' - ')?.[0]?.trim() : productTitle?.trim();
    if (baseTitle && shopifyProducts.length) {
      const exact = shopifyProducts.filter(
        (p) => (p.title || '').toLowerCase() === baseTitle.toLowerCase()
      );
      if (exact.length === 1 && exact[0].image) return exact[0].image;
      if (exact.length > 1 && vid) {
        const hit = exact.find((p) => p.variants?.some((v) => String(v.id) === vid));
        if (hit?.image) return hit.image;
      }
      if (exact.length > 1 && (pid || legacyPid)) {
        const hit = exact.find((p) => {
          const idStr = p.id != null ? String(p.id) : '';
          return (pid && idStr === pid) || (legacyPid && idStr === legacyPid);
        });
        if (hit?.image) return hit.image;
      }
    }

    if (productTitle && shopifyProducts.length) {
      const fuzzy = shopifyProducts.find((product) =>
        productTitle.toLowerCase().includes((product.title || '').toLowerCase())
      );
      if (fuzzy?.image) return fuzzy.image;
    }

    return 'https://via.placeholder.com/150/6B73FF/FFFFFF?text=Custom';
  };

  const TYPE_LABEL_MAP = { tshirt: '👕 T-Shirt', mug: '☕ Mug', poster: '🖼️ Poster', totebag: '👜 Tote Bag', cap: '🧢 Cap', casquette: '🧢 Cap' };

  const findVariantByType = (variants, typeId, preferQuality = 'Standard') => {
    if (!variants?.length) return null;
    const typeLabel = TYPE_LABEL_MAP[typeId];
    const matching = variants.filter(v => {
      const opts = v.selectedOptions || [];
      return !typeLabel || opts.some(o => o.name === 'Type' && o.value === typeLabel);
    });
    const withQuality = matching.find(v => v.selectedOptions?.some(o => o.name === 'Quality' && o.value === preferQuality));
    return withQuality || matching[0];
  };

  const handleProductTypeChangeWithPrice = (typeId) => {
    setMockupProductType(typeId);
    const firstSize = sizeOptionsByType[typeId]?.[0]?.value || '';
    setMockupSize(firstSize);
    setOrderForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        const productTitle = item.title?.split(' - ')?.[0];
        const matchingProduct = shopifyProducts.find(p =>
          p.title.toLowerCase() === productTitle?.toLowerCase()
        );
        if (!matchingProduct) return item;
        const variant = findVariantByType(matchingProduct.variants, typeId);
        if (variant) {
          const typeLabel = TYPE_LABEL_MAP[typeId] || typeId;
          return { ...item, price: variant.price.toString(), variant_id: variant.id, title: `${matchingProduct.title} - ${typeLabel}` };
        }
        return item;
      })
    }));
  };

  // Reset form
  const resetForm = () => {
    setOrderForm({
      lineItems: [{
        title: '',
        price: '',
        quantity: 1,
        grams: 1000,
        variant_id: null
      }],
      currency: 'EUR',
      totalTax: 0,
      email: '',
      note: '',
      productType: '',
      size: '',
      color: '',
      first_name: '',
      last_name: '',
      address1: '',
      phone: '',
      city: '',
      province: '',
      country: '',
      zip: ''
    });
    setMockupProductType('tshirt');
    setMockupSize('XS');
    setMockupColor('#FFFFFF');
    setMockupPositionX(50);
    setMockupPositionY(50);
    setMockupScale(100);
    setCurrentMockupImage(null);
    setCurrentMockupPublicId(null);
    setSelectedProducts([]);
    setActiveCreateDropdownId(null);
  };



  // Create order
  const handleCreateOrder = async () => {
    try {
      setCreatingOrder(true);

      // Validate form
      const hasEmptyFields = orderForm.lineItems.some(item => 
        !item.title.trim() || !item.price || item.quantity < 1
      );

      if (hasEmptyFields) {
        showToastMessage('Please fill in all required fields for line items', 'error');
        setCreatingOrder(false);
        return;
      }

      // Validate required customer information
      if (!orderForm.email || !orderForm.email.trim()) {
        showToastMessage('Customer email is required', 'error');
        setCreatingOrder(false);
        return;
      }

      if (!orderForm.first_name || !orderForm.first_name.trim()) {
        showToastMessage('First name is required', 'error');
        setCreatingOrder(false);
        return;
      }

      if (!orderForm.last_name || !orderForm.last_name.trim()) {
        showToastMessage('Last name is required', 'error');
        setCreatingOrder(false);
        return;
      }

      if (!orderForm.address1 || !orderForm.address1.trim()) {
        showToastMessage('Address is required', 'error');
        setCreatingOrder(false);
        return;
      }

      if (!currentMockupImage) {
        showToastMessage('Please generate a mockup before creating the order', 'error');
        setCreatingOrder(false);
        return;
      }

      // Calculate totals
      const subtotal = orderForm.lineItems.reduce((sum, item) => 
        sum + (parseFloat(item.price) * item.quantity), 0
      );
      const total = subtotal + orderForm.totalTax;

      // Validate order total
      if (total <= 0) {
        showToastMessage('Order total must be greater than zero. Please add products with valid prices.', 'error');
        setCreatingOrder(false);
        return;
      }

      // Format phone number with country code if needed
      const formatPhoneNumber = (phone) => {
        if (!phone) return null;
        // If phone doesn't start with +, assume it's a Philippines number and add +63
        if (phone.startsWith('+')) {
          return phone;
        } else if (phone.startsWith('09')) {
          // Philippines mobile format: 09XXXXXXXXX -> +639XXXXXXXXX
          return '+63' + phone.substring(1);
        } else if (phone.startsWith('9')) {
          // Already in 9XXXXXXXXX format
          return '+63' + phone;
        } else {
          // Default to Philippines code if no country code
          return '+63' + phone;
        }
      };

      const formattedPhone = formatPhoneNumber(orderForm.phone);

      // Get unique vendors from line items
      const vendors = [...new Set(orderForm.lineItems.map(item => item.vendor).filter(Boolean))];
      const vendorStr = vendors.join(', ');

      // Create note_attributes with email, name, and vendor/streamer
      const fullName = `${orderForm.first_name || ""} ${orderForm.last_name || ""}`.trim();
      const noteAttributes = [
        { "name": "email", "value": orderForm.email || "" },
        { "name": "name", "value": fullName }
      ].filter(attr => attr.value.trim() !== "");

      // Add vendor/streamer to note_attributes if available
      if (vendorStr) {
        noteAttributes.push({ "name": "vendor", "value": vendorStr });
        noteAttributes.push({ "name": "streamer", "value": vendorStr });
      }

      // Add product properties from mockup state to note_attributes
      const typeName = productTypes.find(p => p.id === mockupProductType)?.label || mockupProductType;
      if (typeName) noteAttributes.push({ name: "Type", value: typeName });
      if (mockupSize) noteAttributes.push({ name: "Size", value: mockupSize });
      if (mockupColor) noteAttributes.push({ name: "Color", value: mockupColor });
      noteAttributes.push({ name: "PositionX", value: String(mockupPositionX) });
      noteAttributes.push({ name: "PositionY", value: String(mockupPositionY) });

      if (currentMockupImage && currentMockupImage.startsWith('data:')) {
        // Local data URL mockup — upload to Cloudinary now at order creation time
        try {
          const uploadRes = await fetch('/.netlify/functions/cloudinary-upload-mockup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image: currentMockupImage,
              folder: 'merch-platform/order'
            })
          });
          const uploadResult = await uploadRes.json();
          if (uploadResult.success && uploadResult.url) {
            noteAttributes.push({ name: "Sample Image", value: uploadResult.url });
          }
        } catch (uploadErr) {
          console.warn('Mockup upload failed, continuing without:', uploadErr);
        }
      } else if (currentMockupImage) {
        noteAttributes.push({ name: "Sample Image", value: currentMockupImage });
      }

      // Prepare order data for Shopify API
      const orderData = {
        order: {
          currency: orderForm.currency,
          line_items: orderForm.lineItems.map(item => ({
            title: item.title,
            price: parseFloat(item.price),
            grams: parseInt(item.grams),
            quantity: item.quantity,
            variant_id: item.variant_id || null,
            vendor: item.vendor || null,
            tax_lines: orderForm.totalTax > 0 ? [{
              price: orderForm.totalTax,
              rate: orderForm.totalTax / subtotal,
              title: "Tax"
            }] : []
          })),
          transactions: [{
            kind: "sale",
            status: "success",
            amount: total
          }],
          total_tax: orderForm.totalTax,
          email: orderForm.email || null,
          phone: formattedPhone,
          shipping_address: {
            first_name: orderForm.first_name || null,
            last_name: orderForm.last_name || null,
            address1: orderForm.address1 || null,
            phone: formattedPhone,
            city: orderForm.city || null,
            province: orderForm.province || null,
            country: orderForm.country || null,
            zip: orderForm.zip || null
          },
          billing_address: {
            first_name: orderForm.first_name || null,
            last_name: orderForm.last_name || null,
            address1: orderForm.address1 || null,
            phone: formattedPhone,
            city: orderForm.city || null,
            province: orderForm.province || null,
            country: orderForm.country || null,
            zip: orderForm.zip || null
          },
          financial_status: "pending",
          note_attributes: noteAttributes
        }
      };

      console.log('🛒 Creating order:', orderData);

      const response = await fetch('/.netlify/functions/shopify-create-order', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(orderData)
      }));

      const result = await response.json();

      if (result.success) {
        showToastMessage('Order created successfully!', 'success');
        console.log('✅ Order created:', result.order);
        
        // Close modal and reset form
        setShowCreateOrderModal(false);
        resetForm();
        
        // Refresh orders list from Shopify API to get the latest data
        await fetchOrders();
      } else {
        // Extract specific error messages from Shopify API response
        const errorMessage = extractShopifyErrors(result.error || result);
        showToastMessage(errorMessage, 'error');
        console.error('❌ Order creation failed:', result.error || result);
      }
    } catch (error) {
      console.error('❌ Error creating order:', error);
      const errorMessage = extractShopifyErrors(error);
      showToastMessage(`Error creating order: ${errorMessage}`, 'error');
    } finally {
      setCreatingOrder(false);
    }
  };

  const printfulMergedInactiveTab =
    printfulSubTab === 'printful_canceled_order' ||
    printfulSubTab === 'canceled' ||
    printfulSubTab === 'archived';
  const hidePrintfulRowSelection = activeTab === 'printful' && printfulMergedInactiveTab;

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar 
        activeSidebarItem="orders"
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
      />

      {/* Main Content — min-w-0 so wide tables don’t expand the whole page */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-gray-900">
        {/* Top Header Bar */}
        <div className="px-6 py-5 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Orders
              </h1>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              {selectedOrderIds.size > 0 && (
                <button
                  onClick={() => openCancelModal([...selectedOrderIds])}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <span>Cancel ({selectedOrderIds.size})</span>
                </button>
              )}
              <button
                onClick={fetchOrders}
                disabled={loadingOrders}
                className="bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-600 disabled:opacity-50"
                title="Refresh orders"
              >
                {loadingOrders ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
                ) : (
                  <span>Export</span>
                )}
              </button>
              <button
                onClick={() => setShowCreateOrderModal(true)}
                className="bg-gray-100 hover:bg-white text-gray-900 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Create order
              </button>
            </div>
          </div>
        </div>

        {/* Content Area: outer doesn’t scroll; tab panel below handles x + y */}
        <div className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col p-6">
          <div className="flex flex-col flex-1 min-h-0 min-w-0 bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {/* Tab Filters - Shopify style */}
            <div className="border-b border-gray-700 shrink-0 bg-gray-800">
              <div className="flex items-center overflow-x-auto">
                {[
                  { id: 'paid', label: 'Paid' },
                  { id: 'fulfilled', label: 'Fulfilled' },
                  { id: 'unfulfilled', label: 'Unfulfilled' },
                  { id: 'canceled_order', label: 'Canceled order' },
                  { id: 'printful', label: 'Printful' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-white text-white'
                        : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 min-h-0 min-w-0 overflow-x-auto overflow-y-auto sidebar-scroll">
            {activeTab === 'printful' ? (
              <div className="p-6 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-white">Printful orders</h2>
                    <p className="text-sm text-gray-400 mt-1">
                      Manage orders synced to Printful. Click a row for full fulfillment data and tracking.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedPrintfulIds.size > 0 && !hidePrintfulRowSelection && (
                      <button
                        type="button"
                        onClick={cancelSelectedPrintfulOrders}
                        disabled={cancellingPrintful}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center space-x-2"
                      >
                        {cancellingPrintful ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span>Cancelling...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            <span>Cancel order ({selectedPrintfulIds.size})</span>
                          </>
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={fetchPrintfulAdminOrders}
                      disabled={loadingPrintfulAdmin}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50"
                    >
                      {loadingPrintfulAdmin ? 'Loading…' : 'Refresh list'}
                    </button>
                  </div>
                </div>

                {/* Nested sub-tabs */}
                <div className="border-b border-gray-700">
                  <div className="flex items-center">
                    {[
                      { id: 'draft', label: 'Draft' },
                      { id: 'printful_canceled_order', label: 'Printful canceled order' }
                    ].map(st => (
                      <button
                        key={st.id}
                        type="button"
                        onClick={() => { setPrintfulSubTab(st.id); setSelectedPrintfulIds(new Set()); }}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          printfulSubTab === st.id ||
                          (st.id === 'printful_canceled_order' &&
                            (printfulSubTab === 'canceled' || printfulSubTab === 'archived'))
                            ? 'border-purple-500 text-purple-400'
                            : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
                        }`}
                      >
                        {st.label}
                        <span className="ml-1.5 text-xs text-gray-500">
                          ({printfulAdminOrders.filter(o => {
                            if (st.id === 'draft') return o.status === 'draft' || o.status === 'pending';
                            return o.status !== 'draft' && o.status !== 'pending';
                          }).length})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {(() => {
                  const filteredPF = printfulAdminOrders.filter(o => {
                    if (printfulSubTab === 'draft') return o.status === 'draft' || o.status === 'pending';
                    return o.status !== 'draft' && o.status !== 'pending';
                  });
                  const allChecked = filteredPF.length > 0 && filteredPF.every(r => selectedPrintfulIds.has(r.id));
                  const toggleAllPF = () => {
                    if (allChecked) {
                      setSelectedPrintfulIds(new Set());
                    } else {
                      setSelectedPrintfulIds(new Set(filteredPF.map(r => r.id)));
                    }
                  };
                  const toggleOnePF = (id) => {
                    setSelectedPrintfulIds(prev => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id); else next.add(id);
                      return next;
                    });
                  };

                  if (loadingPrintfulAdmin && printfulAdminOrders.length === 0) {
                    return <div className="py-16 text-center text-gray-400 text-sm">Loading Printful orders…</div>;
                  }
                  if (filteredPF.length === 0) {
                    return <div className="py-16 text-center text-gray-500 text-sm">No orders in this category.</div>;
                  }

                  return (
                    <div className="border border-gray-700 rounded-lg min-w-0">
                      <table className="w-full min-w-max text-sm">
                        <thead>
                          <tr className="border-b border-gray-700 bg-gray-900/50">
                            {!hidePrintfulRowSelection && (
                              <th className="px-3 py-3 w-10">
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  onChange={toggleAllPF}
                                  className="h-4 w-4 rounded border-gray-600 bg-gray-900 text-purple-600 focus:ring-purple-500"
                                />
                              </th>
                            )}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Printful ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Shopify order #</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Tracking</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Recipient</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Created</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/80">
                          {filteredPF.map((row) => (
                            <tr
                              key={row.id}
                              role="button"
                              tabIndex={0}
                              onClick={() => openPrintfulRowDetail(row)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openPrintfulRowDetail(row);
                                }
                              }}
                              className={`hover:bg-gray-700/20 cursor-pointer ${
                                !hidePrintfulRowSelection && selectedPrintfulIds.has(row.id) ? 'bg-purple-900/10' : ''
                              }`}
                            >
                              {!hidePrintfulRowSelection && (
                                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedPrintfulIds.has(row.id)}
                                    onChange={() => toggleOnePF(row.id)}
                                    className="h-4 w-4 rounded border-gray-600 bg-gray-900 text-purple-600 focus:ring-purple-500"
                                  />
                                </td>
                              )}
                              <td className="px-4 py-3 text-gray-200 font-mono">{row.id}</td>
                              <td className="px-4 py-3 text-cyan-400 font-mono">{row.external_id ?? '—'}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs capitalize ${
                                  row.status === 'canceled' || row.status === 'failed'
                                    ? 'bg-red-900/30 text-red-400'
                                    : row.status === 'draft' || row.status === 'pending'
                                      ? 'bg-yellow-900/30 text-yellow-400'
                                    : row.status === 'fulfilled' || row.status === 'partial'
                                      ? 'bg-emerald-900/40 text-emerald-300'
                                    : row.status === 'archived'
                                      ? 'bg-gray-600/50 text-gray-300'
                                      : 'bg-gray-700 text-gray-200'
                                }`}>{row.status}</span>
                              </td>
                              <td className="px-4 py-3 text-gray-300 text-xs max-w-[220px] truncate" title={row.tracking_summary || ''}>
                                {row.tracking_summary ? (
                                  <span className="text-cyan-300/90">{row.tracking_summary}</span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-300">{row.recipient_name || '—'}</td>
                              <td className="px-4 py-3 text-gray-400">
                                {row.created ? new Date(row.created * 1000).toLocaleString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            ) : (
            <>
            {/* Search Bar + Filter icons */}
            <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
              <div className="flex-1 relative">
                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:ring-1 focus:ring-gray-500 focus:border-gray-500 transition-colors"
                  placeholder="Filter orders"
                />
              </div>
              <button
                onClick={fetchOrders}
                disabled={loadingOrders}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Refresh"
              >
                <svg className={`w-4 h-4 ${loadingOrders ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {/* Orders Table */}
            {loadingOrders ? (
              <div className="p-16 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-4"></div>
                <p className="text-gray-400 text-sm">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-16 text-center">
                <svg className="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <h3 className="text-sm font-medium text-white mb-1">
                  {allItems.length === 0 ? 'No orders or drafts yet' : 'No matching orders'}
                </h3>
                <p className="text-sm text-gray-500">
                  {allItems.length === 0 
                    ? 'When you receive orders or create drafts, they will appear here.'
                    : 'Try changing the filters or search term.'
                  }
                </p>
              </div>
            ) : (
              <div className="min-w-0">
                <table className="w-full min-w-max">
                  <thead>
                    <tr className="border-b border-gray-700">
                      {activeTab !== 'canceled_order' && (
                        <th className="px-3 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={filteredOrders.length > 0 && selectedOrderIds.size === filteredOrders.length}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-gray-500 bg-gray-700 text-pink-500 focus:ring-pink-500 focus:ring-offset-0 cursor-pointer"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Vendor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Payment status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Fulfillment status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Items</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Delivery method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-400"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {filteredOrders.map((order) => {
                      const isDraft = order.isDraft === true;
                      const payBadge = isDraft ? getDraftStatusBadge(order.status) : getPaymentBadge(order.financialStatus);
                      const fulBadge = getFulfillmentBadge(order.fulfillmentStatus);
                      return (
                        <tr 
                          key={order.id} 
                          className={`hover:bg-gray-750 hover:bg-gray-700/30 transition-colors cursor-pointer group ${selectedOrderIds.has(order.id) ? 'bg-gray-700/40' : ''}`}
                          onClick={() => openViewModal(order)}
                        >
                          {/* Checkbox (hidden on Canceled order tab) */}
                          {activeTab !== 'canceled_order' && (
                            <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedOrderIds.has(order.id)}
                                onChange={() => toggleOrderSelection(order.id)}
                                className="w-4 h-4 rounded border-gray-500 bg-gray-700 text-pink-500 focus:ring-pink-500 focus:ring-offset-0 cursor-pointer"
                              />
                            </td>
                          )}
                          {/* Order # + badges in one horizontal row on Canceled order tab */}
                          <td
                            className={`px-4 py-3 align-middle ${
                              activeTab === 'canceled_order' ? 'min-w-[12rem]' : 'whitespace-nowrap'
                            }`}
                          >
                            <div
                              className={`flex items-center ${
                                activeTab === 'canceled_order'
                                  ? 'flex-nowrap gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                                  : 'flex-wrap gap-x-1 gap-y-1'
                              }`}
                            >
                              <span className="shrink-0 text-sm font-medium text-blue-400 group-hover:underline">
                                {order.orderNumber}
                              </span>
                              {activeTab === 'canceled_order' && <OrderLifecycleBadges order={order} />}
                            </div>
                          </td>
                          {/* Date */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{formatDate(order.createdAt)}</span>
                          </td>
                          {/* Customer */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{getCustomerName(order)}</span>
                          </td>
                          {/* Vendor */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              const vendors = [...new Set((order.lineItems || []).map(item => item.vendor).filter(Boolean))];
                              const noteVendor = (order.note_attributes || []).find(a => a.name === 'vendor' || a.name === 'streamer');
                              const vendorDisplay = vendors.length > 0 ? vendors.join(', ') : noteVendor?.value || '';
                              return vendorDisplay ? (
                                <span className="inline-flex items-center gap-1 text-xs bg-cyan-900/40 text-cyan-400 px-2 py-0.5 rounded-full font-medium">
                                  <i className="fas fa-user text-[10px]"></i>
                                  {vendorDisplay}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-600">—</span>
                              );
                            })()}
                          </td>
                          {/* Total */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-300">
                              {order.currency === 'EUR' ? '€' : order.currency === 'USD' ? '$' : order.currency === 'GBP' ? '£' : ''}
                              {parseFloat(order.totalPrice || 0).toFixed(2)}
                            </span>
                          </td>
                          {/* Payment Status */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${payBadge.bg} ${payBadge.text}`}>
                              {payBadge.complete ? (
                                <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <span className={`w-1.5 h-1.5 rounded-full ${payBadge.dot}`}></span>
                              )}
                              {payBadge.label}
                            </span>
                          </td>
                          {/* Fulfillment Status (or — for drafts) */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isDraft ? (
                              <span className="text-gray-500 text-xs">—</span>
                            ) : (
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${fulBadge.bg} ${fulBadge.text}`}>
                                {fulBadge.complete ? (
                                  <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <span className={`w-1.5 h-1.5 rounded-full ${fulBadge.dot}`}></span>
                                )}
                                {fulBadge.label}
                              </span>
                            )}
                          </td>
                          {/* Items */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-300">{getItemsCount(order.lineItems)}</span>
                          </td>
                          {/* Delivery method */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-400">{getDeliveryMethod(order)}</span>
                          </td>
                          {/* Type (from line item properties or note_attributes) */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              // Get Type from line item properties first
                              const lineItemWithType = (order.lineItems || []).find(item => 
                                (item.properties || []).some(p => p.name === 'Type')
                              );
                              const lineItemTypeProperty = lineItemWithType?.properties?.find(p => p.name === 'Type');
                              // Fall back to note_attributes
                              const noteAttrType = (order.note_attributes || []).find(p => p.name === 'Type');
                              const typeValue = lineItemTypeProperty?.value || noteAttrType?.value || order.tags;
                              return typeValue ? (
                                <span className="text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded">{typeValue}</span>
                              ) : (
                                <span className="text-sm text-gray-600">—</span>
                              );
                            })()}
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!isDraft && (
                                <button
                                  onClick={() => openEditModal(order)}
                                  className="text-gray-400 hover:text-white p-1.5 hover:bg-gray-600 rounded transition-colors"
                                  title="Edit"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                              )}
                              {isDraft && order.invoiceUrl && (
                                <a
                                  href={order.invoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-cyan-400 p-1.5 hover:bg-gray-600 rounded transition-colors"
                                  title="Open invoice"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Order Modal */}
      {showCreateOrderModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col border border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-8 py-6 flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Create Order</h2>
                    <p className="text-pink-100 mt-1">Add products and order details for Shopify</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCreateOrderModal(false)}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 overflow-y-auto sidebar-scroll flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Products */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Products</h3>
                    
                    {/* Product search and browse */}
                    <div className="mb-6">
                      <div className="flex space-x-3">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            placeholder="Search products"
                            onClick={() => setShowProductBrowser(true)}
                            readOnly
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 pl-10 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors cursor-pointer"
                          />
                          <svg className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <button 
                          onClick={() => setShowProductBrowser(true)}
                          className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-4 py-3 rounded-lg border border-gray-600 transition-colors"
                        >
                          Browse
                        </button>
                        <button 
                          onClick={() => setShowCustomItemModal(true)}
                          className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white px-4 py-3 rounded-lg transition-all duration-200"
                        >
                          Add custom item
                        </button>
                      </div>
                    </div>

                    {/* Product List */}
                    {orderForm.lineItems.length > 0 && orderForm.lineItems.some(item => item.title.trim() !== '') && (
                      <div className="mb-6">
                        {/* Product Headers */}
                        <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-400 mb-3 px-4">
                          <div className="col-span-6">Product</div>
                          <div className="col-span-2 text-center">Quantity</div>
                          <div className="col-span-3 text-right">Total</div>
                          <div className="col-span-1"></div>
                        </div>

                        {/* Product Items */}
                        <div className="space-y-3">
                          {orderForm.lineItems.filter(item => item.title.trim() !== '').map((item, index) => (
                            <div key={index} className="grid grid-cols-12 gap-4 items-center py-3 px-4 bg-gray-800 rounded-lg border border-gray-600">
                              {/* Product Info */}
                              <div className="col-span-6 flex items-center space-x-3">
                                <img
                                  src={getProductImage(item)}
                                  alt={item.title}
                                  className="w-10 h-10 rounded-lg object-cover bg-gray-700"
                                  onError={(e) => {
                                    e.target.src = "https://via.placeholder.com/150/6B73FF/FFFFFF?text=Product";
                                  }}
                                />
                                <div>
                                  <h4 className="text-white font-medium text-sm">{item.title}</h4>
                                  <div className="flex items-center gap-2">
                                    <p className="text-gray-400 text-xs">
                                      {orderForm.currency === 'EUR' ? '€' : orderForm.currency === 'USD' ? '$' : '£'}
                                      {parseFloat(item.price || 0).toFixed(2)}
                                    </p>
                                    {item.vendor && (
                                      <span className="text-xs bg-cyan-900/50 text-cyan-400 px-1.5 py-0.5 rounded">
                                        {item.vendor}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Quantity */}
                              <div className="col-span-2 flex justify-center">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => handleLineItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                                  className="w-16 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-center text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                />
                              </div>

                              {/* Total */}
                              <div className="col-span-3 text-right">
                                <span className="text-white font-medium">
                                  {orderForm.currency === 'EUR' ? '€' : orderForm.currency === 'USD' ? '$' : '£'}
                                  {(parseFloat(item.price || 0) * item.quantity).toFixed(2)}
                                </span>
                              </div>

                              {/* Remove Button */}
                              <div className="col-span-1 flex justify-end">
                                <button
                                  onClick={() => removeLineItem(index)}
                                  className="text-gray-400 hover:text-red-400 p-1 hover:bg-red-900/20 rounded transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Summary */}
                    <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-600">
                      <h4 className="text-lg font-semibold text-white mb-4">Payment</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between text-gray-300">
                          <span>Subtotal</span>
                          <span>
                            {orderForm.currency === 'EUR' ? '€' : orderForm.currency === 'USD' ? '$' : '£'}
                            {orderForm.lineItems.reduce((sum, item) => 
                              sum + (parseFloat(item.price || 0) * item.quantity), 0
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Add discount</span>
                          <span className="text-gray-500">—</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Add shipping or delivery</span>
                          <span className="text-gray-500">—</span>
                        </div>
                        <div className="flex justify-between text-gray-300">
                          <span>Estimated tax</span>
                          <span>
                            {orderForm.currency === 'EUR' ? '€' : orderForm.currency === 'USD' ? '$' : '£'}
                            {orderForm.totalTax.toFixed(2)}
                          </span>
                        </div>
                        <div className="border-t border-gray-600 pt-3">
                          <div className="flex justify-between text-white text-xl font-bold">
                            <span>Total</span>
                            <span>
                              {orderForm.currency === 'EUR' ? '€' : orderForm.currency === 'USD' ? '$' : '£'}
                              {(orderForm.lineItems.reduce((sum, item) => 
                                sum + (parseFloat(item.price || 0) * item.quantity), 0
                              ) + orderForm.totalTax).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shipping and Billing Information */}
                    <div className="mt-6 bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                      <h3 className="text-lg font-semibold text-white mb-4">Shipping and Billing Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            First Name <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={orderForm.first_name}
                            onChange={(e) => handleFormChange('first_name', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter first name"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Last Name <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={orderForm.last_name}
                            onChange={(e) => handleFormChange('last_name', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter last name"
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Customer Email <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="email"
                            value={orderForm.email}
                            onChange={(e) => handleFormChange('email', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter customer email"
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Address <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={orderForm.address1}
                            onChange={(e) => handleFormChange('address1', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter address"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                          <input
                            type="tel"
                            value={orderForm.phone}
                            onChange={(e) => handleFormChange('phone', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter phone number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                          <input
                            type="text"
                            value={orderForm.city}
                            onChange={(e) => handleFormChange('city', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter city"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Province</label>
                          <input
                            type="text"
                            value={orderForm.province}
                            onChange={(e) => handleFormChange('province', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter province"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                          <input
                            type="text"
                            value={orderForm.country}
                            onChange={(e) => handleFormChange('country', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter country"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">ZIP Code</label>
                          <input
                            type="text"
                            value={orderForm.zip}
                            onChange={(e) => handleFormChange('zip', e.target.value)}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            placeholder="Enter ZIP code"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Order Details */}
                <div className="space-y-6">
                  {/* Generate Mockup */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <i className="fa-solid fa-shirt mr-2 text-purple-400"></i>
                      Generate Mockup
                    </h3>
                    <p className="text-gray-400 text-xs font-medium mb-3">Select a product type</p>
                    <div className="divide-y divide-gray-600 border border-gray-600 rounded-lg overflow-hidden">
                      {(getSelectedLineItemTypes() || getAvailableTypes(orderForm.lineItems.find(i => i.availableProducts)?.availableProducts)).map((product) => (
                        <button
                          key={product.id}
                          onClick={() => openCreateMockupEditor(product.id)}
                          className="w-full flex items-center gap-2 px-4 py-3 bg-gray-800 hover:bg-purple-600/30 transition-colors duration-150 text-left"
                        >
                          <span className="text-lg">{product.icon}</span>
                          <span className="text-white font-medium text-sm capitalize">{product.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Saved Mockup Preview */}
                  {currentMockupImage && (
                    <div className="bg-gray-700/50 rounded-xl p-6 border border-purple-500/50">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                        <i className="fa-solid fa-image mr-2 text-purple-400"></i>
                        Mockup Preview
                      </h3>
                      <div className="space-y-4">
                        <div className="rounded-lg overflow-hidden bg-gray-800 border border-gray-600">
                          <img 
                            src={currentMockupImage} 
                            alt="Mockup Preview" 
                            className="w-full h-auto"
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => { setCurrentMockupImage(null); setCurrentMockupPublicId(null); }}
                            className="flex-1 px-4 py-3 bg-gray-600 hover:bg-red-600 text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2"
                          >
                            <i className="fa-solid fa-trash"></i>
                            <span>Delete</span>
                          </button>
                          <button
                            onClick={handleEditCreateMockup}
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2"
                          >
                            <i className="fa-solid fa-pencil"></i>
                            <span>Edit Mockup</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Currency */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Currency</h3>
                    <select
                      value={orderForm.currency}
                      onChange={(e) => handleFormChange('currency', e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                    >
                      <option value="EUR">Euro (EUR €)</option>
                      <option value="USD">US Dollar (USD $)</option>
                      <option value="GBP">British Pound (GBP £)</option>
                    </select>
                  </div>

                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-700 px-8 py-6 border-t border-gray-600 flex-shrink-0 rounded-b-2xl">
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowCreateOrderModal(false)}
                  className="px-6 py-3 border border-gray-600 rounded-lg text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={creatingOrder}
                  className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {creatingOrder ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating Order...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      <span>Create Order</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditOrderModal && editingOrder && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col border border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Edit Order</h2>
                    <p className="text-blue-100 mt-1">Update order details for {editingOrder.orderNumber}</p>
                  </div>
                </div>
                <button 
                  onClick={closeEditModal}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 overflow-y-auto sidebar-scroll flex-1">
              <div className="space-y-8">
                {/* Order Information */}
                <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                                     <div className="flex items-center space-x-2 mb-4">
                     <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                       <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                       </svg>
                     </div>
                     <h3 className="text-lg font-semibold text-white">Order Information</h3>
                   </div>
                   
                   {/* Info Notice */}
                   <div className="bg-blue-900/20 border border-blue-600/50 rounded-lg p-4 mb-6">
                     <div className="flex items-start space-x-3">
                       <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                       </svg>
                       <div>
                         <h4 className="text-blue-400 font-medium text-sm">GraphQL API Enabled</h4>
                         <p className="text-blue-200 text-xs mt-1">
                           <strong>Editable:</strong> Email, phone, notes, tags, shipping address, custom attributes (Type, Size, Color)<br/>
                           <strong>Read-only:</strong> Order number, total, financial status, line item details
                         </p>
                       </div>
                     </div>
                   </div>
                  
                                     {/* Read-only order info */}
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Order Number</label>
                       <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-400">
                         {editingOrder.orderNumber}
                       </div>
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
                       <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-400">
                         {editingOrder.currency} ({editingOrder.currency === 'EUR' ? '€' : editingOrder.currency === 'USD' ? '$' : '£'})
                       </div>
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Total Price</label>
                       <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-400">
                         {editingOrder.currency === 'EUR' ? '€' : editingOrder.currency === 'USD' ? '$' : '£'}
                         {editingOrder.totalPrice}
                       </div>
                     </div>
                   </div>

                                     {/* Editable customer info */}
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Customer Email</label>
                       <input
                         type="email"
                         value={orderForm.email}
                         onChange={(e) => handleFormChange('email', e.target.value)}
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                         placeholder="customer@example.com"
                       />
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Customer Phone</label>
                       <input
                         type="tel"
                         value={orderForm.phone || ''}
                         onChange={(e) => handleFormChange('phone', e.target.value)}
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                         placeholder="+1234567890"
                       />
                     </div>
                   </div>
                   
                   {/* Sample Image / Mockup Preview */}
                   <div className="mt-6 p-4 bg-gray-700/50 rounded-xl border border-purple-500/50">
                     <div className="flex items-center justify-between mb-3">
                       <h4 className="text-white font-medium flex items-center">
                         <i className="fa-solid fa-image mr-2 text-purple-400"></i>
                         Sample Image
                       </h4>
                       <button
                         type="button"
                         onClick={openMockupEditor}
                         className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all flex items-center space-x-2"
                       >
                         <i className="fa-solid fa-pencil"></i>
                         <span>{currentMockupImage || (editingOrder?.note_attributes?.find(a => a.name === 'Sample Image' || a.name === 'MockupImage')?.value) ? 'Edit Mockup' : 'Create Mockup'}</span>
                       </button>
                     </div>
                     {(currentMockupImage || editingOrder?.note_attributes?.find(a => a.name === 'Sample Image' || a.name === 'MockupImage')?.value) ? (
                       <div className="rounded-lg overflow-hidden bg-gray-800 border border-gray-600 max-w-xs">
                         <img 
                           src={currentMockupImage || editingOrder?.note_attributes?.find(a => a.name === 'Sample Image' || a.name === 'MockupImage')?.value} 
                           alt="Sample Mockup" 
                           className="w-full h-auto"
                           onError={(e) => {
                             e.target.src = "https://via.placeholder.com/300/6B73FF/FFFFFF?text=Sample+Image";
                           }}
                         />
                       </div>
                     ) : (
                       <div className="rounded-lg bg-gray-800 border border-dashed border-gray-600 p-8 text-center">
                         <i className="fa-solid fa-image text-3xl text-gray-500 mb-2"></i>
                         <p className="text-gray-400 text-sm">No sample image yet</p>
                         <p className="text-gray-500 text-xs mt-1">Click "Create Mockup" to generate one</p>
                       </div>
                     )}
                   </div>

                   {/* Additional editable fields */}
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Order Tags</label>
                       <input
                         type="text"
                         value={orderForm.tags || ''}
                         onChange={(e) => handleFormChange('tags', e.target.value)}
                         className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                         placeholder="tag1, tag2, tag3"
                       />
                       <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-gray-300 mb-2">Marketing Consent</label>
                       <div className="flex items-center space-x-3 h-12">
                         <label className="flex items-center space-x-2 cursor-pointer">
                           <input
                             type="checkbox"
                             checked={orderForm.buyerAcceptsMarketing || false}
                             onChange={(e) => handleFormChange('buyerAcceptsMarketing', e.target.checked)}
                             className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                           />
                           <span className="text-gray-300 text-sm">Customer accepts marketing</span>
                         </label>
                       </div>
                     </div>
                   </div>
                                     <div className="mt-6">
                     <label className="block text-sm font-medium text-gray-300 mb-2">Order Notes</label>
                     <textarea
                       value={orderForm.note}
                       onChange={(e) => handleFormChange('note', e.target.value)}
                       rows={3}
                       className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                       placeholder="Add any special instructions or notes for this order..."
                     />
                   </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-700 px-8 py-6 border-t border-gray-600 flex-shrink-0 rounded-b-2xl">
              <div className="flex justify-end space-x-4">
                <button
                  onClick={closeEditModal}
                  className="px-6 py-3 border border-gray-600 rounded-lg text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditOrder}
                  disabled={creatingOrder}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {creatingOrder ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating Order...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Update Order</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Browser Modal */}
      {showProductBrowser && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">Select products</h2>
              <button 
                onClick={cancelProductSelection}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search and Filter */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <select className="border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="all">Search by All</option>
                </select>
                <button
                  onClick={fetchShopifyProducts}
                  disabled={loadingProducts}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  title="Refresh products"
                >
                  <svg className={`w-5 h-5 ${loadingProducts ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button className="text-sm text-blue-600 hover:text-blue-800">Add filter +</button>
                <span className="text-sm text-gray-500">
                  {shopifyProducts.length} products loaded
                </span>
              </div>
            </div>

            {/* Products List */}
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              {loadingProducts ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-gray-600">Loading products...</span>
                  </div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2.09M4 13v4a2 2 0 002 2h4m0 0v-4a2 2 0 012-2h4" />
                  </svg>
                  <p className="text-gray-600">
                    {productSearch ? `No products found for "${productSearch}"` : 'No products available'}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="space-y-2">
                      {/* Product Header */}
                      <div className="flex items-center space-x-3">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-10 h-10 rounded-lg object-cover bg-gray-100 border border-gray-200"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/150/E5E7EB/6B7280?text=Product";
                          }}
                        />
                        <div>
                          <span className="font-medium text-gray-900">{product.title}</span>
                          {product.vendor && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                              {product.vendor}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Product Variants */}
                      <div className="ml-13 space-y-1">
                        {product.variants.map((variant) => (
                          <div key={variant.id} className="flex items-center justify-between py-2 hover:bg-gray-50 rounded-lg px-2">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={isVariantSelected(product, variant)}
                                onChange={(e) => handleVariantSelection(product, variant, e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-gray-700">{variant.title}</span>
                            </div>
                            <div className="flex items-center space-x-4 text-sm">
                              <span className={`${variant.available === 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                {variant.available} available
                              </span>
                              <span className="text-gray-900 font-medium">
                                €{variant.price.toFixed(2)} {variant.currency}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0 rounded-b-xl">
              <span className="text-sm text-gray-600">
                {selectedProducts.length}/500 variants selected
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={cancelProductSelection}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={addSelectedProductsToOrder}
                  disabled={selectedProducts.length === 0}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Item Modal */}
      {showCustomItemModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add custom item</h2>
              <button 
                onClick={cancelCustomItem}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Item Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Item name</label>
                <input
                  type="text"
                  value={customItem.name}
                  onChange={(e) => handleCustomItemChange('name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter item name"
                />
              </div>

              {/* Price and Quantity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 text-sm">€</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={customItem.price}
                      onChange={(e) => handleCustomItemChange('price', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={customItem.quantity}
                    onChange={(e) => handleCustomItemChange('quantity', parseInt(e.target.value) || 1)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customItem.taxable}
                    onChange={(e) => handleCustomItemChange('taxable', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Item is taxable</span>
                </label>

                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customItem.physical}
                    onChange={(e) => handleCustomItemChange('physical', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Item is a physical product</span>
                </label>
              </div>

              {/* Weight (only show if physical product) */}
              {customItem.physical && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Item weight (optional)</label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={customItem.weight}
                      onChange={(e) => handleCustomItemChange('weight', parseFloat(e.target.value) || 0)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0"
                    />
                    <span className="flex items-center px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-700">
                      kg
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Used to calculate shipping rates accurately</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={cancelCustomItem}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addCustomItemToOrder}
                disabled={!customItem.name.trim() || !customItem.price}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Order Modal */}
      {showViewModal && selectedViewOrder && (() => {
        const vo = selectedViewOrder;
        const viewModalShopifyLocked =
          !vo.isDraft &&
          (isShopifyOrderCanceled(vo) || isOrderRefunded(vo) || isOrderArchived(vo));
        const pfStatus = (selectedViewOrderPrintful?.status || '').toLowerCase();
        const fulfillDisabledPrintful =
          !selectedViewOrderPrintful?.id ||
          ['fulfilled', 'inprocess', 'inprogress'].includes(pfStatus) ||
          ['canceled', 'cancelled', 'failed', 'archived'].includes(pfStatus);
        const fulfillDisabled = fulfillDisabledPrintful || viewModalShopifyLocked;
        return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col border border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-6 py-4 flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-white">Order Details</h2>
                    <p className="text-pink-100 text-sm">{vo.orderNumber}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {vo.isDraft ? (
                        <span className="inline-flex items-center rounded-md border border-amber-400/40 bg-amber-950/40 px-2 py-0.5 text-[11px] font-semibold leading-none text-amber-100">
                          Draft
                        </span>
                      ) : (
                        <>
                          <OrderLifecycleBadges order={vo} />
                          {(() => {
                            const b = getFulfillmentBadge(vo.fulfillmentStatus);
                            return (
                              <span
                                className={`inline-flex shrink-0 items-center gap-1 rounded-md border border-white/25 bg-white/15 px-2 py-0.5 text-[11px] font-semibold leading-none text-white`}
                              >
                                {b.complete ? (
                                  <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
                                ) : (
                                  <span className="h-1.5 w-1.5 rounded-full border border-white/80 bg-transparent" />
                                )}
                                {b.label}
                              </span>
                            );
                          })()}
                          {!isOrderRefunded(vo) && (() => {
                            const b = getPaymentBadge(vo.financialStatus);
                            return (
                              <span
                                className={`inline-flex shrink-0 items-center gap-1 rounded-md border border-white/25 bg-white/15 px-2 py-0.5 text-[11px] font-semibold leading-none text-white`}
                              >
                                {b.complete ? (
                                  <svg className="h-3 w-3 text-white/95 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                                )}
                                {b.label}
                              </span>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button 
                  onClick={closeViewModal}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content - Tabs */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Tab Headers */}
              <div className="border-b border-gray-600 px-6 flex-shrink-0">
                <div className="flex gap-1">
                  <button
                    onClick={() => setOrderDetailTab('shopify')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      orderDetailTab === 'shopify'
                        ? 'border-pink-500 text-white'
                        : 'border-transparent text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    Shopify Order
                  </button>
                  {(selectedViewOrderPrintful || loadingPrintful) && (
                    <button
                      onClick={() => setOrderDetailTab('printful')}
                      className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        orderDetailTab === 'printful'
                          ? 'border-pink-500 text-white'
                          : 'border-transparent text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      Printful Order
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6 overflow-y-auto sidebar-scroll flex-1">
              {orderDetailTab === 'shopify' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Order Info */}
                <div className="space-y-6">
                  {/* Order Summary */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Order Number:</span>
                        <span className="text-white font-medium">{selectedViewOrder.orderNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Order ID:</span>
                        <span className="text-white font-medium">{selectedViewOrder.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Date Created:</span>
                        <span className="text-white font-medium">{formatDate(selectedViewOrder.createdAt)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Vendor/Streamer:</span>
                        {(() => {
                          const vendors = [...new Set((selectedViewOrder.lineItems || []).map(item => item.vendor).filter(Boolean))];
                          const noteVendor = (selectedViewOrder.note_attributes || []).find(a => a.name === 'vendor' || a.name === 'streamer');
                          const vendorDisplay = vendors.length > 0 ? vendors.join(', ') : noteVendor?.value || '';
                          return vendorDisplay ? (
                            <span className="inline-flex items-center gap-1.5 text-sm bg-cyan-900/40 text-cyan-400 px-2.5 py-1 rounded-full font-medium">
                              <i className="fas fa-user text-xs"></i>
                              {vendorDisplay}
                            </span>
                          ) : (
                            <span className="text-gray-500">Not specified</span>
                          );
                        })()}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Total Amount:</span>
                        <span className="text-white font-bold text-lg">
                          {selectedViewOrder.currency === 'EUR' ? '€' : selectedViewOrder.currency === 'USD' ? '$' : '£'}
                          {selectedViewOrder.totalPrice}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Customer Information</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Email:</span>
                        <span className="text-white font-medium">{selectedViewOrder.email || 'No email provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Phone:</span>
                        <span className="text-white font-medium">{selectedViewOrder.phone || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Order Status */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Order Status</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Payment Status:</span>
                        {(() => { const b = getPaymentBadge(selectedViewOrder.financialStatus); return (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${b.bg} ${b.text}`}>
                            {b.complete ? (
                              <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`}></span>
                            )}
                            {b.label}
                          </span>
                        ); })()}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Fulfillment Status:</span>
                        {(() => { const b = getFulfillmentBadge(selectedViewOrder.fulfillmentStatus); return (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${b.bg} ${b.text}`}>
                            {b.complete ? (
                              <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`}></span>
                            )}
                            {b.label}
                          </span>
                        ); })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Products & Sample Image */}
                <div className="space-y-6">
                  {/* Line Items */}
                  <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-4">Products ({selectedViewOrder.lineItems.length})</h3>
                    <div className="space-y-4">
                      {selectedViewOrder.lineItems.map((item, index) => {
                        // Extract properties from line items first, then fall back to note_attributes
                        const lineItemProps = item.properties || [];
                        const noteAttrs = selectedViewOrder.note_attributes || [];
                        
                        const getProperty = (name) => {
                          const lineItemProp = lineItemProps.find(p => p.name === name);
                          if (lineItemProp) return lineItemProp;
                          const noteProp = noteAttrs.find(p => p.name === name);
                          return noteProp || null;
                        };
                        
                        const typeProperty = getProperty('Type');
                        const sizeProperty = getProperty('Size');
                        const colorProperty = getProperty('Color');
                        const qualityProperty = getProperty('Quality');
                        const positionXProperty = getProperty('PositionX');
                        const positionYProperty = getProperty('PositionY');
                        const productIdDisplay =
                          item.product_legacy_id ||
                          item.product_id ||
                          null;

                        return (
                          <div key={index} className="p-4 bg-gray-800 rounded-lg border border-gray-600">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={getProductImage(item)}
                                  alt={item.title}
                                  className="w-12 h-12 rounded-lg object-cover bg-gray-700"
                                  onError={(e) => {
                                    e.target.src = "https://via.placeholder.com/150/6B73FF/FFFFFF?text=Product";
                                  }}
                                />
                                <div>
                                  <h4 className="text-white font-medium text-sm">{item.title}</h4>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <p className="text-gray-400 text-xs">
                                      Qty: {item.quantity}
                                    </p>
                                    {item.vendor && (
                                      <span className="text-xs bg-cyan-900/40 text-cyan-400 px-1.5 py-0.5 rounded font-medium">
                                        {item.vendor}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-white font-medium">
                                  {selectedViewOrder.currency === 'EUR' ? '€' : selectedViewOrder.currency === 'USD' ? '$' : '£'}
                                  {(parseFloat(item.price || 0) * item.quantity).toFixed(2)}
                                </p>
                                <p className="text-gray-400 text-xs">
                                  {selectedViewOrder.currency === 'EUR' ? '€' : selectedViewOrder.currency === 'USD' ? '$' : '£'}
                                  {parseFloat(item.price || 0).toFixed(2)} each
                                </p>
                              </div>
                            </div>
                            {/* Product Properties */}
                            {(typeProperty || sizeProperty || colorProperty || qualityProperty || positionXProperty || positionYProperty || productIdDisplay) && (
                              <div className="mt-3 pt-3 border-t border-gray-700 grid grid-cols-2 gap-x-6">
                                {/* Left: Type, Color, Size, Quality */}
                                <div className="space-y-2">
                                  {typeProperty && (
                                    <div className="flex items-center gap-2">
                                      <i className="fa-solid fa-shirt text-purple-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">Type:</span>
                                      <span className="text-white text-xs font-medium">{typeProperty.value}</span>
                                    </div>
                                  )}
                                  {colorProperty && (
                                    <div className="flex items-center gap-2">
                                      <i className="fa-solid fa-palette text-pink-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">Color:</span>
                                      <span 
                                        className="w-4 h-4 rounded border border-gray-600" 
                                        style={{ backgroundColor: colorProperty.value }}
                                      ></span>
                                      <span className="text-white text-xs font-medium uppercase">{colorProperty.value}</span>
                                    </div>
                                  )}
                                  {sizeProperty && (
                                    <div className="flex items-center gap-2">
                                      <i className="fa-solid fa-ruler text-blue-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">Size:</span>
                                      <span className="text-white text-xs font-medium">{sizeProperty.value}</span>
                                    </div>
                                  )}
                                  {qualityProperty && (
                                    <div className="flex items-center gap-2">
                                      <i className="fa-solid fa-gem text-amber-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">Quality:</span>
                                      <span className="text-white text-xs font-medium">{qualityProperty.value}</span>
                                    </div>
                                  )}
                                </div>
                                {/* Right: Y Position, X Position */}
                                <div className="space-y-2">
                                  {positionYProperty && (
                                    <div className="flex items-center gap-2">
                                      <i className="fa-solid fa-arrows-alt-v text-cyan-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">Y Position:</span>
                                      <span className="text-white text-xs font-medium">{positionYProperty.value}%</span>
                                    </div>
                                  )}
                                  {positionXProperty && (
                                    <div className="flex items-center gap-2">
                                      <i className="fa-solid fa-arrows-alt-h text-cyan-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">X Position:</span>
                                      <span className="text-white text-xs font-medium">{positionXProperty.value}%</span>
                                    </div>
                                  )}
                                  {productIdDisplay && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <i className="fa-solid fa-barcode text-emerald-400 text-xs"></i>
                                      <span className="text-gray-400 text-xs">Product ID:</span>
                                      <span className="text-white text-xs font-medium font-mono">{String(productIdDisplay)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sample Image */}
                  {(() => {
                    const noteAttrs = selectedViewOrder.note_attributes || [];
                    const sampleImage = noteAttrs.find(a => a.name === 'Sample Image' || a.name === 'MockupImage');
                    if (!sampleImage?.value) return null;
                    return (
                      <div className="bg-gray-700/50 rounded-xl p-6 border border-purple-500/50">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                          <i className="fa-solid fa-image mr-2 text-purple-400"></i>
                          Sample Image
                        </h3>
                        <div className="rounded-lg overflow-hidden bg-gray-800 border border-gray-600">
                          <img 
                            src={sampleImage.value} 
                            alt="Sample Mockup" 
                            className="w-full h-auto"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/400/6B73FF/FFFFFF?text=Sample+Image";
                            }}
                          />
                        </div>
                      </div>
                    );
                  })()}

                </div>
              </div>
              ) : (
              /* Printful Order Tab */
              <div className="space-y-6">
                {loadingPrintful ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
                  </div>
                ) : selectedViewOrderPrintful ? (() => {
                  const pf = selectedViewOrderPrintful;
                  return (
                    <>
                      <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                        <h3 className="text-lg font-semibold text-white mb-4">Printful Order Summary</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Printful Order ID:</span>
                            <span className="text-white font-medium">{pf.id}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">External ID:</span>
                            <span className="text-white font-medium">{pf.external_id || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Status:</span>
                            <span className="text-white font-medium capitalize">{pf.status || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Shipping:</span>
                            <span className="text-white font-medium">{pf.shipping_service_name || pf.shipping || '-'}</span>
                          </div>
                        </div>
                      </div>
                      {pf.recipient && (
                        <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                          <h3 className="text-lg font-semibold text-white mb-4">Recipient</h3>
                          <div className="space-y-2 text-gray-300">
                            <p>{pf.recipient.name}</p>
                            <p>{pf.recipient.address1}</p>
                            {pf.recipient.address2 && <p>{pf.recipient.address2}</p>}
                            <p>{pf.recipient.city}, {pf.recipient.state_code} {pf.recipient.zip}</p>
                            <p>{pf.recipient.country_name || pf.recipient.country_code}</p>
                            {pf.recipient.phone && <p>Phone: {pf.recipient.phone}</p>}
                          </div>
                        </div>
                      )}
                      {pf.items && pf.items.length > 0 && (
                        <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                          <h3 className="text-lg font-semibold text-white mb-4">Items ({pf.items.length})</h3>
                          <div className="space-y-4">
                            {pf.items.map((item, i) => (
                              <div key={i} className="p-4 bg-gray-800 rounded-lg border border-gray-600">
                                <div className="flex justify-between">
                                  <span className="text-white font-medium">{item.name || `Product ${i + 1}`}</span>
                                  <span className="text-gray-400">Qty: {item.quantity}</span>
                                </div>
                                {item.product?.name && <p className="text-gray-400 text-sm mt-1">{item.product.name}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {pf.costs && (
                        <div className="bg-gray-700/50 rounded-xl p-6 border border-gray-600">
                          <h3 className="text-lg font-semibold text-white mb-4">Costs</h3>
                          <div className="space-y-2 text-gray-300">
                            <p>Subtotal: {pf.costs.currency} {pf.costs.subtotal}</p>
                            <p>Shipping: {pf.costs.currency} {pf.costs.shipping}</p>
                            <p className="text-white font-medium">Total: {pf.costs.currency} {pf.costs.total}</p>
                          </div>
                        </div>
                      )}
                      {Array.isArray(pf.shipments) && pf.shipments.length > 0 && (
                        <div className="bg-gray-700/50 rounded-xl p-6 border border-emerald-600/40">
                          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <i className="fa-solid fa-truck-fast text-emerald-400" />
                            Fulfillment & tracking
                          </h3>
                          <div className="space-y-4">
                            {pf.shipments.map((s, idx) => (
                              <div
                                key={s.id ?? idx}
                                className="p-4 bg-gray-800 rounded-lg border border-gray-600 text-sm"
                              >
                                <div className="flex flex-wrap justify-between gap-2 text-gray-200">
                                  <span className="font-medium">
                                    {[s.carrier, s.service].filter(Boolean).join(' · ') || 'Shipment'}
                                  </span>
                                  {s.ship_date && (
                                    <span className="text-gray-400">Ship date: {s.ship_date}</span>
                                  )}
                                </div>
                                {s.tracking_number != null &&
                                  String(s.tracking_number) !== '' &&
                                  String(s.tracking_number) !== '0' && (
                                    <p className="mt-2 text-cyan-300 font-mono">
                                      {s.tracking_url ? (
                                        <a
                                          href={s.tracking_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="underline hover:text-cyan-200"
                                        >
                                          {s.tracking_number}
                                        </a>
                                      ) : (
                                        s.tracking_number
                                      )}
                                    </p>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <div className="space-y-4 py-6">
                    <p className="text-gray-400">No Printful order found for this Shopify order.</p>
                    <div className="flex flex-wrap gap-3 items-center">
                      <span className="text-gray-400 text-sm">Or enter Printful Order ID from dashboard:</span>
                      <input
                        type="text"
                        value={printfulOrderIdInput}
                        onChange={(e) => setPrintfulOrderIdInput(e.target.value)}
                        placeholder="e.g. 12345678"
                        className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white w-40"
                      />
                      <button
                        onClick={fetchByPrintfulOrderId}
                        disabled={!printfulOrderIdInput?.trim() || loadingPrintful}
                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-sm"
                      >
                        Fetch
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-700 px-6 py-4 border-t border-gray-600 flex-shrink-0 rounded-b-2xl">
              <div className="flex justify-between items-center">
                <div className="flex space-x-3">
                  <button
                    onClick={() => fulfillOrder(selectedViewOrder, selectedViewOrderPrintful)}
                    disabled={fulfillDisabled}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <span>
                      {viewModalShopifyLocked
                        ? 'Fulfill unavailable'
                        : !selectedViewOrderPrintful?.id
                          ? (loadingPrintful ? 'Loading...' : 'No Printful')
                          : ['fulfilled', 'inprocess', 'inprogress'].includes(pfStatus)
                            ? 'Already fulfilled'
                            : ['canceled', 'cancelled', 'failed'].includes(pfStatus)
                              ? 'Printful canceled'
                              : pfStatus === 'archived'
                                ? 'Printful archived'
                                : 'Fulfill via Printful'}
                    </span>
                  </button>
                  {!selectedViewOrderPrintful?.id && !loadingPrintful && (
                    <button
                      onClick={refreshPrintful}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                      title="Search Printful by order ID and customer email"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Refresh from Printful</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (viewModalShopifyLocked) return;
                      closeViewModal();
                      openEditModal(selectedViewOrder);
                    }}
                    disabled={viewModalShopifyLocked}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit Order</span>
                  </button>
                  {!selectedViewOrder?.isDraft &&
                    !isShopifyOrderCanceled(selectedViewOrder) &&
                    !isOrderRefunded(selectedViewOrder) &&
                    !isOrderArchived(selectedViewOrder) && (
                    <button
                      type="button"
                      onClick={() => openCancelModal([selectedViewOrder.id])}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      <span>Cancel Order</span>
                    </button>
                  )}
                </div>
                <button
                  onClick={closeViewModal}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      );
      })()}

      {/* Printful row detail (fulfillment / tracking) */}
      {printfulRowDetailModal.open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4"
          onClick={closePrintfulRowDetail}
        >
          <div
            className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto border border-gray-600 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Printful fulfillment</h3>
              <button
                type="button"
                onClick={closePrintfulRowDetail}
                className="text-gray-400 hover:text-white p-1"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              {printfulRowDetailModal.loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500" />
                </div>
              ) : printfulRowDetailModal.order ? (
                (() => {
                  const pf = printfulRowDetailModal.order;
                  return (
                    <div className="space-y-4 text-sm">
                      <div className="flex justify-between text-gray-300">
                        <span>Printful ID</span>
                        <span className="text-white font-mono">{pf.id}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Shopify external #</span>
                        <span className="text-cyan-400 font-mono">{pf.external_id ?? '—'}</span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Status</span>
                        <span className="text-white capitalize">{pf.status || '—'}</span>
                      </div>
                      {Array.isArray(pf.shipments) && pf.shipments.length > 0 ? (
                        <div className="pt-2 border-t border-gray-700">
                          <p className="text-white font-medium mb-3">Shipments</p>
                          <ul className="space-y-3">
                            {pf.shipments.map((s, idx) => (
                              <li
                                key={s.id ?? idx}
                                className="bg-gray-900/80 rounded-lg p-3 border border-gray-600"
                              >
                                <p className="text-gray-200">
                                  {[s.carrier, s.service].filter(Boolean).join(' · ') || 'Shipment'}
                                </p>
                                {s.ship_date && (
                                  <p className="text-gray-500 text-xs mt-1">Ship date: {s.ship_date}</p>
                                )}
                                {s.tracking_number != null &&
                                  String(s.tracking_number) !== '' &&
                                  String(s.tracking_number) !== '0' && (
                                    <p className="mt-2 font-mono text-cyan-300">
                                      {s.tracking_url ? (
                                        <a
                                          href={s.tracking_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="underline"
                                        >
                                          Track: {s.tracking_number}
                                        </a>
                                      ) : (
                                        <>Tracking: {s.tracking_number}</>
                                      )}
                                    </p>
                                  )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm pt-2 border-t border-gray-700">
                          No shipments yet — status: <span className="text-gray-300">{pf.status}</span>
                        </p>
                      )}
                    </div>
                  );
                })()
              ) : null}
            </div>
            <div className="px-6 py-3 border-t border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={closePrintfulRowDetail}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Printful bulk cancel confirmation */}
      {showPrintfulCleanupConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-600">
            <h3 className="text-xl font-bold text-white mb-2">Remove eligible Printful orders?</h3>
            <p className="text-gray-400 text-sm mb-4">
              Tries API v2 delete first (permanent removal when Printful allows). Uses API v1 cancel as fallback. Only draft, pending, failed, or canceled rows are attempted; in-production orders are skipped.
            </p>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowPrintfulCleanupConfirm(false)}
                disabled={printfulCleanupRunning}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={executePrintfulCleanupAll}
                disabled={printfulCleanupRunning}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {printfulCleanupRunning ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block" />
                    Working…
                  </>
                ) : (
                  'Confirm remove'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fulfill Order Confirmation Modal */}
      {showFulfillOrderModal && orderToFulfill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4 border border-gray-600">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Fulfill Order</h3>
                <p className="text-gray-400 text-sm">Mark this order as fulfilled</p>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-gray-300 mb-3">Are you sure you want to fulfill this order via Printful?</p>
              <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <p className="text-white font-medium">{orderToFulfill.order?.orderNumber || orderToFulfill.order?.name || `Order #${orderToFulfill.order?.id}`}</p>
                {orderToFulfill.order?.email && <p className="text-gray-400 text-sm mt-1">{orderToFulfill.order.email}</p>}
              </div>
            </div>
            <div className="flex space-x-3">
              <button onClick={() => { setShowFulfillOrderModal(false); setOrderToFulfill(null); }} className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-medium transition-colors">
                Cancel
              </button>
              <button onClick={confirmFulfillOrder} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors">
                Fulfill Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mockup Editor Modal */}
      {showMockupEditorModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[10001] flex items-center justify-center p-4"
          onClick={closeMockupEditor}
        >
          <div 
            className="relative bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-shirt text-white text-xl"></i>
                <h3 className="text-white font-bold text-lg">{mockupEditorContext === 'create' ? 'Mockup Preview' : 'Edit Mockup'}</h3>
              </div>
              <button
                onClick={closeMockupEditor}
                className="text-white hover:text-gray-200 transition-colors p-2"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-5">
                {/* Product Type Selector */}
                <div className="flex items-center justify-center">
                  <div className="flex space-x-2">
                    {(getSelectedLineItemTypes() || getAvailableTypes((orderForm.lineItems || []).find(i => i.availableProducts)?.availableProducts)).map((type) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          handleProductTypeChangeWithPrice(type.id);
                          setMockupSize('');
                        }}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                          mockupProductType === type.id
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {type.icon} {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 2-Column Layout: Preview + Controls */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Column 1: Preview */}
                  <div className="space-y-3">
                    <div ref={mockupPreviewRef} className="bg-gray-800 rounded-xl p-4 shadow-lg">
                      <div className="relative w-full overflow-hidden rounded-lg bg-gray-700">
                        <div className="relative">
                          {/* SVG filter for product colorization */}
                          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
                            <defs>
                              <filter id="adminProductColorFilter">
                                <feColorMatrix
                                  type="matrix"
                                  values={(() => {
                                    const hex = (mockupColor.startsWith('#') ? mockupColor : '#FFFFFF').replace('#', '').padEnd(6, '0').substring(0, 6);
                                    const r = (parseInt(hex.substring(0, 2), 16) || 255) / 255;
                                    const g = (parseInt(hex.substring(2, 4), 16) || 255) / 255;
                                    const b = (parseInt(hex.substring(4, 6), 16) || 255) / 255;
                                    return `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`;
                                  })()}
                                />
                              </filter>
                            </defs>
                          </svg>
                          {/* Product template with color tint */}
                          {productTemplates[mockupProductType]?.image ? (
                            <img 
                              src={productTemplates[mockupProductType].image} 
                              alt={productTemplates[mockupProductType].title}
                              className="w-full h-auto rounded-lg"
                              style={{ filter: (mockupColor.startsWith('#') && mockupColor !== '#FFFFFF') ? 'url(#adminProductColorFilter)' : 'none' }}
                            />
                          ) : (
                            <div style={{ paddingBottom: '100%' }} className="bg-gray-600 rounded-lg"></div>
                          )}
                          {/* Design overlay */}
                          {(() => {
                            const designImage = mockupEditorContext === 'create'
                              ? (orderForm.lineItems || []).find(item => item.image)?.image
                              : editingOrder?.lineItems?.[0]
                                ? getProductImage(editingOrder.lineItems[0])
                                : null;
                            return designImage;
                          })() && (
                            <div className="absolute inset-0 pointer-events-none">
                              <img 
                                src={mockupEditorContext === 'create'
                                  ? (orderForm.lineItems || []).find(item => item.image)?.image
                                  : getProductImage(editingOrder?.lineItems?.[0])}
                                alt="Design"
                                style={{
                                  position: 'absolute',
                                  left: `${mockupPositionX}%`,
                                  top: `${mockupPositionY}%`,
                                  transform: `translate(-50%, -50%) scale(${mockupScale / 100})`,
                                  maxWidth: '50%',
                                  maxHeight: '50%',
                                  objectFit: 'contain',
                                  transition: 'all 0.15s ease-out',
                                  opacity: 0.85,
                                  filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.5))',
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {/* Crosshair guides */}
                              <div style={{ position: 'absolute', left: `${mockupPositionX}%`, top: 0, bottom: 0, width: '1px', background: 'rgba(168,85,247,0.3)' }}></div>
                              <div style={{ position: 'absolute', top: `${mockupPositionY}%`, left: 0, right: 0, height: '1px', background: 'rgba(168,85,247,0.3)' }}></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Column 2: Controls */}
                  <div className="space-y-4">
                    {/* Position & Size Controls */}
                    <div className="bg-gray-800 rounded-xl p-5 border-2 border-purple-500/30 space-y-4">
                      <h4 className="text-white font-semibold text-sm flex items-center">
                        <i className="fa-solid fa-crosshairs mr-2 text-purple-400"></i>
                        Position & Size
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-400 text-xs font-medium">↔️ X Position</span>
                            <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{mockupPositionX}%</span>
                          </div>
                          <input type="range" min="0" max="100" value={mockupPositionX} onChange={(e) => setMockupPositionX(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-400 text-xs font-medium">↕️ Y Position</span>
                            <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{mockupPositionY}%</span>
                          </div>
                          <input type="range" min="0" max="100" value={mockupPositionY} onChange={(e) => setMockupPositionY(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-400 text-xs font-medium">📐 Scale</span>
                            <span className="text-purple-400 text-xs font-bold bg-gray-700 px-2 py-0.5 rounded">{mockupScale}%</span>
                          </div>
                          <input type="range" min="10" max="200" value={mockupScale} onChange={(e) => setMockupScale(parseInt(e.target.value))} className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500" />
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button onClick={() => { setMockupPositionX(50); setMockupPositionY(50); setMockupScale(100); }} className="flex-1 px-3 py-2 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center space-x-1">
                          <i className="fa-solid fa-rotate-left"></i>
                          <span>Reset</span>
                        </button>
                        <button onClick={() => { setMockupPositionX(50); setMockupPositionY(50); }} className="flex-1 px-3 py-2 bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-600 hover:to-cyan-600 text-white text-xs font-medium rounded-lg transition-all flex items-center justify-center space-x-1">
                          <i className="fa-solid fa-bolt"></i>
                          <span>Center</span>
                        </button>
                      </div>
                    </div>

                    {/* Size Selection */}
                    <div className="bg-gray-800 rounded-xl p-5 border border-gray-600">
                      <h4 className="text-white font-semibold text-sm flex items-center mb-3">
                        <i className="fa-solid fa-ruler mr-2 text-blue-400"></i>
                        Size <span className="text-red-400 ml-1">*</span>
                      </h4>
                      <select
                        value={mockupSize}
                        onChange={(e) => setMockupSize(e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      >
                        <option value="">Select size...</option>
                        {(sizeOptionsByType[mockupProductType] || []).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Color Selection */}
                    <div className="bg-gray-800 rounded-xl p-5 border border-gray-600">
                      <h4 className="text-white font-semibold text-sm flex items-center mb-3">
                        <i className="fa-solid fa-palette mr-2 text-pink-400"></i>
                        Color
                      </h4>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={mockupColor.startsWith('#') ? mockupColor : '#FFFFFF'}
                          onChange={(e) => setMockupColor(e.target.value)}
                          className="h-10 w-14 rounded-lg border border-gray-600 bg-gray-700 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={mockupColor}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMockupColor(v.startsWith('#') ? v : v ? '#' + v : '#FFFFFF');
                          }}
                          placeholder="#FFFFFF"
                          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-mono"
                        />
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-800 px-6 py-4 border-t border-gray-700 flex-shrink-0 rounded-b-2xl">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeMockupEditor}
                  className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMockup}
                  disabled={savingMockup || !mockupSize}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {savingMockup ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i>
                      <span>{mockupEditorContext === 'create' ? 'Proceed' : 'Save Mockup'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelOrderModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl w-full max-w-lg mx-4 border border-gray-600 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">
                Cancel order{cancellingOrderIds.length > 1 ? `s (${cancellingOrderIds.length})` : cancellingOrderIds.length === 1 ? ` #${allItems.find(o => o.id === cancellingOrderIds[0])?.orderNumber || cancellingOrderIds[0]}` : ''}?
              </h3>
              <button onClick={() => setShowCancelOrderModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto sidebar-scroll">
              {/* Refund method */}
              <div>
                <label className="block text-sm font-semibold text-white mb-3">Refund payments</label>
                <div className="space-y-2">
                  {[
                    { value: 'original', label: 'Refund to original payment method' },
                    { value: 'store_credit', label: 'Store credit' },
                    { value: 'later', label: 'Later (no refund now)' }
                  ].map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        cancelForm.refundMethod === opt.value
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-600 bg-gray-900/50 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="refundMethod"
                        value={opt.value}
                        checked={cancelForm.refundMethod === opt.value}
                        onChange={(e) => setCancelForm(prev => ({ ...prev, refundMethod: e.target.value }))}
                        className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-200">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Cancellation reason */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Reason for cancellation</label>
                <select
                  value={cancelForm.reason}
                  onChange={(e) => setCancelForm(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="customer">Customer changed or canceled order</option>
                  <option value="fraud">Fraudulent order</option>
                  <option value="inventory">Items unavailable</option>
                  <option value="declined">Payment declined</option>
                  <option value="staff">Staff</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Staff note */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Staff note</label>
                <input
                  type="text"
                  value={cancelForm.staffNote}
                  onChange={(e) => setCancelForm(prev => ({ ...prev, staffNote: e.target.value }))}
                  placeholder=""
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Only you and other staff can see this note.</p>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cancelForm.restock}
                    onChange={(e) => setCancelForm(prev => ({ ...prev, restock: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-200">Restock inventory</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cancelForm.notifyCustomer}
                    onChange={(e) => setCancelForm(prev => ({ ...prev, notifyCustomer: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-200">Send a notification to the customer</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelOrderModal(false)}
                disabled={cancellingInProgress}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                Keep order
              </button>
              <button
                onClick={confirmCancelOrders}
                disabled={cancellingInProgress}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {cancellingInProgress ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Cancelling...</span>
                  </>
                ) : (
                  <span>Cancel order{cancellingOrderIds.length > 1 ? 's' : ''}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed bottom-4 right-4 z-[10000] transform transition-all duration-500 ease-in-out ${
          showToast ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg min-w-[300px] max-w-md ${
            toastType === 'success' 
              ? 'bg-green-600 text-white' 
              : toastType === 'info' 
              ? 'bg-blue-600 text-white'
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {toastType === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : toastType === 'info' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrder; 